import axios, { AxiosInstance, AxiosResponse, AxiosError } from 'axios';
import { LoginResponse } from '../types/auth';
import { SupaSQResponse } from '../types/supa';
import { Customer } from '../types/customer';
import { AccountAsset, AccountAssetCreate, AccountAssetUpdate, AccountDocument } from '../types/account';
import {
  OrderProduct,
  OrderHardware,
  OrderAddon,
  OrderSubcategory,
  CategoryWithSubcategories,
  OrderSubmit,
  OrderResponse,
  SubcategoryProductField,
  SubcategoryProductFieldItem,
  ProductFieldDefinition,
  UpstreamCarrier,
} from '../types/order';
import {
  DomainRegistryInfo,
  DomainSyncResult,
  DomainSyncLogList,
  DomainDnsZoneResponse,
  SynergyDomainListResponse,
  SynergyImportItem,
  SynergyImportResponse,
  SynergyTestConnectionResponse,
} from '../types/domainRegistry';
import {
  TaskMetaResponse,
  TaskListResponse,
  TaskDetailResponse,
  TaskCreateRequest,
  TaskUpdateRequest,
  TaskTypeAdmin,
  TaskStatusAdmin,
  TaskModuleSettingsOut,
  TaskModuleSettingsUpdate,
} from '../types/task';

class ApiService {
  private api: AxiosInstance;
  private baseURL: string;

  constructor() {
    this.baseURL = process.env.REACT_APP_API_URL || window.location.origin;
    
    this.api = axios.create({
      baseURL: this.baseURL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add auth token
    this.api.interceptors.request.use(
      (config) => {
        const token = this.getToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        // Default instance Content-Type is application/json; multipart uploads need boundary — let axios set it
        if (config.data instanceof FormData) {
          config.headers.delete?.('Content-Type');
          delete (config.headers as Record<string, unknown>)['Content-Type'];
          delete (config.headers as Record<string, unknown>)['content-type'];
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor to handle 401 errors
    this.api.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        if (error.response?.status === 401) {
          this.clearToken();
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  // Token management
  getToken(): string | null {
    return localStorage.getItem('access_token');
  }

  setToken(token: string): void {
    localStorage.setItem('access_token', token);
  }

  clearToken(): void {
    localStorage.removeItem('access_token');
  }

  // Authentication
  async login(username: string, password: string, totpCode?: string): Promise<LoginResponse> {
    const payload: any = { username, password };
    if (totpCode) {
      payload.totp_code = totpCode;
    }
    
    const response: AxiosResponse<LoginResponse> = await this.api.post(
      '/api/auth/login',
      payload
    );
    return response.data;
  }

  async getCurrentUser(): Promise<any> {
    const response = await this.api.get('/api/auth/me');
    return response.data;
  }

  /** Return new access_token; updates stored token on success. */
  async refreshAccessToken(): Promise<string> {
    const response = await this.api.post<LoginResponse>('/api/auth/refresh', {});
    const { access_token } = response.data;
    this.setToken(access_token);
    return access_token;
  }

  // Customer management
  async getCustomers(filters: any): Promise<any> {
    const params = new URLSearchParams();
    if (filters.search) params.append('search', filters.search);
    if (filters.status) params.append('status', filters.status);
    if (filters.service_type) params.append('service_type', filters.service_type);
    if (filters.skip !== undefined) params.append('skip', filters.skip.toString());
    if (filters.limit !== undefined) params.append('limit', filters.limit.toString());
    
    const response = await this.api.get(`/api/customers/?${params}`);
    return response.data;
  }

  async getCustomer(id: string): Promise<Customer> {
    const response = await this.api.get(`/api/customers/${id}`);
    return response.data;
  }

  async createCustomer(customer: Partial<Customer>): Promise<Customer> {
    const response = await this.api.post('/api/customers/', customer);
    return response.data;
  }

  async updateCustomer(id: string, customer: Partial<Customer>): Promise<Customer> {
    const response = await this.api.put(`/api/customers/${id}`, customer);
    return response.data;
  }

  async deleteCustomer(id: string): Promise<void> {
    await this.api.delete(`/api/customers/${id}`);
  }

  async exportCustomersCSV(filters: any): Promise<Blob> {
    const params = new URLSearchParams();
    if (filters.search) params.append('search', filters.search);
    if (filters.status) params.append('status', filters.status);
    if (filters.service_type) params.append('service_type', filters.service_type);
    
    const response = await this.api.get(`/api/customers/export/csv/?${params}`, {
      responseType: 'blob',
    });
    return response.data;
  }

  async importCustomersCSV(file: File): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await this.api.post('/api/customers/import-csv/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  // Outage management
  async getOutages(params?: URLSearchParams): Promise<any[]> {
    const queryString = params ? `?${params.toString()}` : '';
    const response = await this.api.get(`/api/outages/${queryString}`);
    return response.data.outages || response.data || []; // Extract outages array from response
  }

  async getOutageStats(): Promise<any> {
    const response = await this.api.get('/api/outages/statistics');
    return response.data;
  }

  async getOutageDetails(incidentId: string): Promise<any> {
    const response = await this.api.get(`/api/outages/${incidentId}`);
    return response.data;
  }

  async getNextIncidentId(network?: string): Promise<string> {
    const params = network ? `?network=${encodeURIComponent(network)}` : '';
    const response = await this.api.get(`/api/outages/next-incident-id${params}`);
    return response.data.incident_id;
  }

  async createNbnOutage(outage: any): Promise<any> {
    const response = await this.api.post('/api/outages/', outage);
    return response.data;
  }

  async updateNbnOutage(outageId: string, outage: any): Promise<any> {
    const response = await this.api.patch(`/api/outages/${outageId}`, outage);
    return response.data;
  }

  async deleteNbnOutage(outageId: string): Promise<void> {
    await this.api.delete(`/api/outages/${outageId}`);
  }


  // Mobile Outages
  async getMobileOutages(params?: Record<string, string>): Promise<any[]> {
    const queryString = params ? '?' + new URLSearchParams(params).toString() : '';
    const response = await this.api.get(`/api/mobile-outages/${queryString}`);
    return response.data;
  }

  async getMobileOutageDetails(outageId: string): Promise<any> {
    const response = await this.api.get(`/api/mobile-outages/${outageId}`);
    return response.data;
  }

  async getMobileOutageTimeline(outageId: string): Promise<any> {
    const response = await this.api.get(`/api/mobile-outages/${outageId}/timeline`);
    return response.data;
  }

  async createMobileOutage(outage: any): Promise<any> {
    const response = await this.api.post('/api/mobile-outages/', outage);
    return response.data;
  }

  async updateMobileOutage(outageId: string, outage: any): Promise<any> {
    const response = await this.api.patch(`/api/mobile-outages/${outageId}`, outage);
    return response.data;
  }

  async deleteMobileOutage(outageId: string): Promise<void> {
    await this.api.delete(`/api/mobile-outages/${outageId}`);
  }

  async bulkDeleteMobileOutages(ids: string[]): Promise<{ deleted: number }> {
    const response = await this.api.post('/api/mobile-outages/bulk-delete', { ids });
    return response.data;
  }

  async bulkResolveMobileOutages(ids: string[]): Promise<{ resolved: number }> {
    const response = await this.api.post('/api/mobile-outages/bulk-resolve', { ids });
    return response.data;
  }

  // Voice Outages
  async getVoiceOutages(params?: Record<string, string>): Promise<any> {
    const queryString = params && Object.keys(params).length > 0 ? new URLSearchParams(params).toString() : '';
    // Always use trailing slash to avoid 307 redirect
    const url = queryString ? `/api/voice-outages/?${queryString}` : '/api/voice-outages/';
    const response = await this.api.get(url);
    return response.data;
  }

  async generateVoiceIncidentId(): Promise<string> {
    const response = await this.api.get('/api/voice-outages/generate-incident-id');
    return response.data.incident_id;
  }

  async getVoiceOutage(outageId: string): Promise<any> {
    const response = await this.api.get(`/api/voice-outages/${outageId}`);
    return response.data;
  }

  async createVoiceOutage(outage: any): Promise<any> {
    const response = await this.api.post('/api/voice-outages/', outage);
    return response.data;
  }

  async updateVoiceOutage(outageId: string, outage: any): Promise<any> {
    const response = await this.api.put(`/api/voice-outages/${outageId}`, outage);
    return response.data;
  }

  async deleteVoiceOutage(outageId: string): Promise<void> {
    await this.api.delete(`/api/voice-outages/${outageId}`);
  }

  async getVoiceOutageTimeline(outageId: string): Promise<any[]> {
    const response = await this.api.get(`/api/voice-outages/${outageId}/timeline`);
    return response.data;
  }

  async addVoiceOutageTimelineEntry(outageId: string, entry: any): Promise<any> {
    const response = await this.api.post(`/api/voice-outages/${outageId}/timeline`, entry);
    return response.data;
  }

  // Web Services Outages
  async getWebServicesOutages(params?: Record<string, string>): Promise<any> {
    const queryString = params && Object.keys(params).length > 0 ? new URLSearchParams(params).toString() : '';
    // Always use trailing slash to avoid 307 redirect
    const url = queryString ? `/api/web-services-outages/?${queryString}` : '/api/web-services-outages/';
    const response = await this.api.get(url);
    return response.data;
  }

  async generateWebServicesIncidentId(): Promise<string> {
    const response = await this.api.get('/api/web-services-outages/generate-incident-id');
    return response.data.incident_id;
  }

  async getWebServicesOutage(outageId: string): Promise<any> {
    const response = await this.api.get(`/api/web-services-outages/${outageId}`);
    return response.data;
  }

  async createWebServicesOutage(outage: any): Promise<any> {
    const response = await this.api.post('/api/web-services-outages/', outage);
    return response.data;
  }

  async updateWebServicesOutage(outageId: string, outage: any): Promise<any> {
    const response = await this.api.put(`/api/web-services-outages/${outageId}`, outage);
    return response.data;
  }

  async deleteWebServicesOutage(outageId: string): Promise<void> {
    await this.api.delete(`/api/web-services-outages/${outageId}`);
  }

  async getWebServicesOutageTimeline(outageId: string): Promise<any[]> {
    const response = await this.api.get(`/api/web-services-outages/${outageId}/timeline`);
    return response.data;
  }

  async addWebServicesOutageTimelineEntry(outageId: string, entry: any): Promise<any> {
    const response = await this.api.post(`/api/web-services-outages/${outageId}/timeline`, entry);
    return response.data;
  }

  // Health check
  async healthCheck(): Promise<any> {
    const response = await this.api.get('/health');
    return response.data;
  }

  // Generic request method for 2FA endpoints
  async request(endpoint: string, options: any = {}): Promise<any> {
    const config = {
      method: options.method || 'GET',
      url: endpoint,
      data: options.data,
      headers: {
        "Content-Type": "application/json",
        ...options.headers
      },
    };

    const response = await this.api.request(config);
    return response.data;
  }

  async getImpactedCustomers(incidentId: string): Promise<any> {
    const response = await this.api.get(`/api/outages/${incidentId}/impacted-customers`);
    return response.data;
  }

  async getOutageTimeline(incidentId: string): Promise<any> {
    const response = await this.api.get(`/api/outages/${incidentId}/timeline`);
    return response.data;
  }

  async getOutageUpdates(outageId: string): Promise<any[]> {
    const response = await this.api.get(`/api/outages/${outageId}/updates`);
    return response.data;
  }

  async addOutageUpdate(outageId: string, update: { description: string; status?: string; notify_email?: boolean; notify_sms?: boolean }): Promise<any> {
    const response = await this.api.post(`/api/outages/${outageId}/updates`, update);
    return response.data;
  }

  // ---- Customer outage notifications (automatic emails from support@) ----
  async getOutageNotificationSettings(): Promise<any> {
    const response = await this.api.get('/api/admin/outage-notifications/settings');
    return response.data;
  }

  async saveOutageNotificationSettings(body: Record<string, unknown>): Promise<any> {
    const response = await this.api.put('/api/admin/outage-notifications/settings', body);
    return response.data;
  }

  async sendOutageNotificationSample(event = 'created'): Promise<{ sent: number; recipients: number }> {
    const response = await this.api.post('/api/admin/outage-notifications/send-sample', null, { params: { event } });
    return response.data;
  }

  async getOutageEmailTemplates(): Promise<any> {
    const response = await this.api.get('/api/admin/outage-notifications/templates');
    return response.data;
  }

  async saveOutageEmailTemplate(event: string, body: { subject: string; body_html: string }): Promise<any> {
    const response = await this.api.put(`/api/admin/outage-notifications/templates/${event}`, body);
    return response.data;
  }

  async resetOutageEmailTemplate(event: string): Promise<any> {
    const response = await this.api.delete(`/api/admin/outage-notifications/templates/${event}`);
    return response.data;
  }

  async previewOutageEmailTemplate(event: string, body: { subject: string; body_html: string }): Promise<{ subject: string; html: string; text: string }> {
    const response = await this.api.post(`/api/admin/outage-notifications/templates/${event}/preview`, body);
    return response.data;
  }

  async getOutageCustomerNotifications(outageId: string): Promise<{ mode: 'off' | 'test' | 'live'; notify_customers: boolean; items: any[] }> {
    const response = await this.api.get(`/api/outages/${outageId}/customer-notifications`);
    return response.data;
  }

  async setOutageNotifyCustomers(outageId: string, enabled: boolean): Promise<{ notify_customers: boolean }> {
    const response = await this.api.patch(`/api/outages/${outageId}/notify-customers`, { enabled });
    return response.data;
  }

  async notifyOutageEmail(outageId: string): Promise<{ sent: number; failed: number; skipped: number }> {
    const response = await this.api.post(`/api/outages/${outageId}/notify-email`);
    return response.data;
  }

  async notifyOutageSms(outageId: string): Promise<{ sent: number; failed: number; skipped: number }> {
    const response = await this.api.post(`/api/outages/${outageId}/notify-sms`);
    return response.data;
  }

  // System Status APIs
  async getConnectionFailures(): Promise<any> {
    const response = await this.api.get('/api/system/connection-failures');
    return response.data;
  }

  async getCarrierStatus(timeframe_hours: number = 24): Promise<{
    carriers: any[];
    case_mailbox_poller?: any;
    support_desk_mailbox_poller?: any;
    total_carriers: number;
    healthy_carriers: number;
    warning_carriers: number;
    error_carriers: number;
    disabled_carriers: number;
    timestamp: string;
  }> {
    const response = await this.api.get(`/api/system/carrier-status?timeframe_hours=${timeframe_hours}`);
    return response.data;
  }

  async getSystemLogs(params?: {
    page?: number;
    page_size?: number;
    level?: string;
    source?: string;
    start_date?: string;
    end_date?: string;
    search?: string;
  }): Promise<any> {
    const queryParams = new URLSearchParams();
    
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.page_size) queryParams.append('page_size', params.page_size.toString());
    if (params?.level) queryParams.append('level', params.level);
    if (params?.source) queryParams.append('source', params.source);
    if (params?.start_date) queryParams.append('start_date', params.start_date);
    if (params?.end_date) queryParams.append('end_date', params.end_date);
    if (params?.search) queryParams.append('search', params.search);
    
    const response = await this.api.get(`/api/system/logs?${queryParams.toString()}`);
    return response.data;
  }

  // Module Settings API
  async getModuleSettings(): Promise<any> {
    const response = await this.api.get("/api/carriers/modules/");
    return response.data;
  }

  async toggleModule(moduleName: string, isEnabled: boolean): Promise<any> {
    const response = await this.api.post(`/api/carriers/modules/${moduleName}/toggle`, {
      is_enabled: isEnabled
    });
    return response.data;
  }

  async updateModuleSettings(moduleName: string, settings: Record<string, any>): Promise<any> {
    const response = await this.api.put(`/api/carriers/modules/${moduleName}/settings`, {
      settings: settings
    });
    return response.data;
  }

  // Carrier API
  async getCarriers(): Promise<any> {
    const response = await this.api.get("/api/carriers/");
    return response.data;
  }

  async updateCarrierNotificationSettings(carrierId: string, settings: { notify_on_created?: boolean; notify_on_updated?: boolean; notify_on_resolved?: boolean }): Promise<any> {
    const response = await this.api.put(`/api/carriers/${carrierId}/notification-settings`, settings);
    return response.data;
  }

  async getNotificationTemplates(serviceId: string): Promise<any> {
    const response = await this.api.get(`/api/notification-services/${serviceId}/templates`);
    return response.data;
  }

  async updateNotificationTemplates(serviceId: string, templates: Record<string, string>): Promise<any> {
    const response = await this.api.put(`/api/notification-services/${serviceId}/templates`, templates);
    return response.data;
  }

  async testNotificationTemplate(serviceId: string, testData: { event_type: string; title_template?: string; message_template?: string; outage_id?: string; outage_type?: string; outage_data?: any }): Promise<any> {
    const response = await this.api.post(`/api/notification-services/${serviceId}/test-template`, testData);
    return response.data;
  }

  async toggleCarrier(carrierId: string, isEnabled: boolean): Promise<any> {
    const response = await this.api.put(`/api/carriers/${carrierId}/toggle`, {
      is_enabled: isEnabled
    });
    return response.data;
  }

  // Service Types (Settings → Service Types)
  async getServiceTypes(includeFields = false): Promise<any[]> {
    const url = `/api/service-types?include_fields=${includeFields}`;
    const response = await this.api.get(url);
    return response.data;
  }

  async getServiceType(typeId: string): Promise<any> {
    const response = await this.api.get(`/api/service-types/${typeId}`);
    return response.data;
  }

  async createServiceType(payload: { code: string; name: string; sort_order?: number }): Promise<any> {
    const response = await this.api.post('/api/service-types', payload);
    return response.data;
  }

  async updateServiceType(typeId: string, payload: { code?: string; name?: string; sort_order?: number }): Promise<any> {
    const response = await this.api.put(`/api/service-types/${typeId}`, payload);
    return response.data;
  }

  async deleteServiceType(typeId: string): Promise<void> {
    await this.api.delete(`/api/service-types/${typeId}`);
  }

  async uploadServiceTypeLogo(typeId: string, file: File): Promise<any> {
    const form = new FormData();
    form.append('file', file);
    // Let axios set multipart boundary; a manual Content-Type breaks uploads
    const response = await this.api.post(`/api/service-types/${typeId}/logo`, form);
    return response.data;
  }

  async deleteServiceTypeLogo(typeId: string): Promise<any> {
    const response = await this.api.delete(`/api/service-types/${typeId}/logo`);
    return response.data;
  }

  async reorderServiceTypes(items: { id: string; sort_order: number }[]): Promise<any[]> {
    const response = await this.api.post('/api/service-types/reorder', items);
    return response.data;
  }

  async getServiceTypeFields(typeId: string): Promise<any[]> {
    const response = await this.api.get(`/api/service-types/${typeId}/fields`);
    return response.data;
  }

  async createServiceTypeField(typeId: string, payload: { field_key: string; label: string; field_type?: string; required?: boolean; sort_order?: number }): Promise<any> {
    const response = await this.api.post(`/api/service-types/${typeId}/fields`, payload);
    return response.data;
  }

  async updateServiceTypeField(typeId: string, fieldId: string, payload: { field_key?: string; label?: string; field_type?: string; required?: boolean; sort_order?: number }): Promise<any> {
    const response = await this.api.put(`/api/service-types/${typeId}/fields/${fieldId}`, payload);
    return response.data;
  }

  async deleteServiceTypeField(typeId: string, fieldId: string): Promise<void> {
    await this.api.delete(`/api/service-types/${typeId}/fields/${fieldId}`);
  }

  async reorderServiceTypeFields(typeId: string, items: { id: string; sort_order: number }[]): Promise<any[]> {
    const response = await this.api.post(`/api/service-types/${typeId}/fields/reorder`, items);
    return response.data;
  }

  async getFieldOptions(typeId: string, fieldId: string): Promise<any[]> {
    const response = await this.api.get(`/api/service-types/${typeId}/fields/${fieldId}/options`);
    return response.data;
  }

  async createFieldOption(typeId: string, fieldId: string, payload: { value: string; label?: string; sort_order?: number }): Promise<any> {
    const response = await this.api.post(`/api/service-types/${typeId}/fields/${fieldId}/options`, payload);
    return response.data;
  }

  async updateFieldOption(typeId: string, fieldId: string, optionId: string, payload: { value?: string; label?: string; sort_order?: number }): Promise<any> {
    const response = await this.api.put(`/api/service-types/${typeId}/fields/${fieldId}/options/${optionId}`, payload);
    return response.data;
  }

  async deleteFieldOption(typeId: string, fieldId: string, optionId: string): Promise<void> {
    await this.api.delete(`/api/service-types/${typeId}/fields/${fieldId}/options/${optionId}`);
  }

  async reorderFieldOptions(typeId: string, fieldId: string, items: { id: string; sort_order: number }[]): Promise<any[]> {
    const response = await this.api.post(`/api/service-types/${typeId}/fields/${fieldId}/options/reorder`, items);
    return response.data;
  }

  // Account API
  async getAccounts(params?: Record<string, any>): Promise<any> {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, String(value));
        }
      });
    }
    const response = await this.api.get(`/api/accounts/?${queryParams}`);
    return response.data;
  }

  async getAccount(id: string): Promise<any> {
    const response = await this.api.get(`/api/accounts/${id}`);
    return response.data;
  }

  async createAccount(account: any): Promise<any> {
    const response = await this.api.post('/api/accounts/', account);
    return response.data;
  }

  async updateAccount(id: string, account: any): Promise<any> {
    const response = await this.api.put(`/api/accounts/${id}`, account);
    return response.data;
  }

  async deleteAccount(id: string): Promise<void> {
    await this.api.delete(`/api/accounts/${id}`);
  }

  async getAccountStatistics(): Promise<any> {
    const response = await this.api.get('/api/accounts/statistics');
    return response.data;
  }

  async getAccountServices(accountId: string, params?: Record<string, any>): Promise<any> {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, String(value));
        }
      });
    }
    const response = await this.api.get(`/api/accounts/${accountId}/services?${queryParams}`);
    return response.data;
  }

  async getLinkableServices(accountId: string): Promise<{ id: string; label: string; service_type: string }[]> {
    const response = await this.api.get(`/api/accounts/${accountId}/linkable-services`);
    return response.data;
  }

  async getAccountServiceOutages(accountId: string): Promise<{ outages: any[] }> {
    const response = await this.api.get(`/api/accounts/${accountId}/service-outages`);
    return response.data;
  }

  async createAccountService(accountId: string, service: any): Promise<any> {
    const response = await this.api.post(`/api/accounts/${accountId}/services`, service);
    return response.data;
  }

  async getService(serviceId: string): Promise<any> {
    const response = await this.api.get(`/api/accounts/services/${serviceId}`);
    return response.data;
  }

  async updateService(serviceId: string, service: any): Promise<any> {
    const response = await this.api.put(`/api/accounts/services/${serviceId}`, service);
    return response.data;
  }

  async deleteService(serviceId: string): Promise<void> {
    await this.api.delete(`/api/accounts/services/${serviceId}`);
  }

  async revealServicePasswordField(serviceId: string, fieldKey: string): Promise<{ field_key: string; value: string }> {
    const response = await this.api.get(`/api/accounts/services/${serviceId}/reveal-field`, { params: { field_key: fieldKey } });
    return response.data;
  }

  async getMspBalance(serviceId: string): Promise<any> {
    const response = await this.api.get(`/api/accounts/services/${serviceId}/msp-balance`);
    return response.data;
  }

  async getMspActivity(serviceId: string, params?: { page?: number; per_page?: number }): Promise<any> {
    const q = new URLSearchParams();
    if (params?.page) q.set('page', String(params.page));
    if (params?.per_page) q.set('per_page', String(params.per_page));
    const response = await this.api.get(`/api/accounts/services/${serviceId}/msp-activity?${q}`);
    return response.data;
  }

  async postMspActivityNote(serviceId: string, message: string): Promise<any> {
    const response = await this.api.post(`/api/accounts/services/${serviceId}/msp-activity/notes`, { message });
    return response.data;
  }

  async getMspDocuments(serviceId: string): Promise<{ documents: any[] }> {
    const response = await this.api.get(`/api/accounts/services/${serviceId}/msp-documents`);
    return response.data;
  }

  async uploadMspDocument(serviceId: string, formData: FormData): Promise<any> {
    const response = await this.api.post(`/api/accounts/services/${serviceId}/msp-documents`, formData);
    return response.data;
  }

  async updateMspDocument(
    serviceId: string,
    documentId: string,
    body: { title?: string; document_type?: string }
  ): Promise<any> {
    const response = await this.api.put(`/api/accounts/services/${serviceId}/msp-documents/${documentId}`, body);
    return response.data;
  }

  async deleteMspDocument(serviceId: string, documentId: string): Promise<void> {
    await this.api.delete(`/api/accounts/services/${serviceId}/msp-documents/${documentId}`);
  }

  async getMspDocumentFileBlob(serviceId: string, documentId: string, inline = true): Promise<Blob> {
    const response = await this.api.get(
      `/api/accounts/services/${serviceId}/msp-documents/${documentId}/file`,
      { responseType: 'blob', params: { inline: inline ? 'true' : 'false' } }
    );
    return response.data;
  }

  async transferAccountService(
    sourceAccountId: string,
    serviceId: string,
    targetAccountId: string
  ): Promise<any> {
    const response = await this.api.post(
      `/api/accounts/${sourceAccountId}/services/${serviceId}/transfer`,
      { target_account_id: targetAccountId }
    );
    return response.data;
  }

  // -------------------------------------------------------------------------
  // Domain Name service / Synergy Wholesale registry integration.
  // -------------------------------------------------------------------------

  async getDomainRegistryInfo(
    accountServiceId: string,
    options?: { includeAuth?: boolean }
  ): Promise<DomainRegistryInfo> {
    const q = new URLSearchParams();
    if (options?.includeAuth) q.set('include_auth', 'true');
    const qs = q.toString();
    const response = await this.api.get(
      `/api/domains/services/${accountServiceId}${qs ? `?${qs}` : ''}`
    );
    return response.data;
  }

  async syncDomainRegistry(accountServiceId: string): Promise<DomainSyncResult> {
    const response = await this.api.post(`/api/domains/services/${accountServiceId}/sync`);
    return response.data;
  }

  async getDomainSyncLog(
    accountServiceId: string,
    params?: { limit?: number }
  ): Promise<DomainSyncLogList> {
    const q = new URLSearchParams();
    if (params?.limit) q.set('limit', String(params.limit));
    const qs = q.toString();
    const response = await this.api.get(
      `/api/domains/services/${accountServiceId}/sync-log${qs ? `?${qs}` : ''}`
    );
    return response.data;
  }

  async getDomainDnsZone(accountServiceId: string): Promise<DomainDnsZoneResponse> {
    const response = await this.api.get(`/api/domains/services/${accountServiceId}/dns-zone`);
    return response.data;
  }

  async testSynergyConnection(): Promise<SynergyTestConnectionResponse> {
    const response = await this.api.get(`/api/domains/synergy/test`);
    return response.data;
  }

  async listSynergyDomains(params?: {
    page?: number;
    limit?: number;
    status?: string;
  }): Promise<SynergyDomainListResponse> {
    const q = new URLSearchParams();
    if (params?.page) q.set('page', String(params.page));
    if (params?.limit) q.set('limit', String(params.limit));
    if (params?.status) q.set('status', params.status);
    const qs = q.toString();
    const response = await this.api.get(`/api/domains/synergy/list${qs ? `?${qs}` : ''}`);
    return response.data;
  }

  async importSynergyDomains(items: SynergyImportItem[]): Promise<SynergyImportResponse> {
    const response = await this.api.post(`/api/domains/synergy/import`, { items });
    return response.data;
  }

  // Tasks module
  async getInternetServicesReport(params: Record<string, string | undefined>): Promise<any> {
    const clean = Object.fromEntries(Object.entries(params).filter(([, v]) => v));
    const response = await this.api.get('/api/admin/reports/internet-services', { params: clean });
    return response.data;
  }

  // ---- Financial reports (admin only): category is 'internet' or 'mobile' ----
  async getFinancialReport(category: 'internet' | 'mobile', params: Record<string, string | undefined>): Promise<any> {
    const clean = Object.fromEntries(Object.entries(params).filter(([, v]) => v));
    const response = await this.api.get(`/api/admin/reports/${category}-financial`, { params: clean });
    return response.data;
  }

  async getFinancialPlanCosts(category: 'internet' | 'mobile'): Promise<any[]> {
    const response = await this.api.get(`/api/admin/reports/${category}-financial/plan-costs`);
    return response.data;
  }

  async getServicePricingOverride(category: 'internet' | 'mobile', serviceId: string): Promise<{ monthly_price: number | null; monthly_cost: number | null; note: string | null }> {
    const response = await this.api.get(`/api/admin/reports/${category}-financial/overrides/${serviceId}`);
    return response.data;
  }

  async setServicePricingOverride(category: 'internet' | 'mobile', serviceId: string, body: { monthly_price: string | null; monthly_cost: string | null; note: string | null }): Promise<void> {
    await this.api.put(`/api/admin/reports/${category}-financial/overrides/${serviceId}`, body);
  }

  async deleteServicePricingOverride(category: 'internet' | 'mobile', serviceId: string): Promise<void> {
    await this.api.delete(`/api/admin/reports/${category}-financial/overrides/${serviceId}`);
  }

  async setMobileServicePlan(serviceId: string, productId: string | null): Promise<void> {
    await this.api.put(`/api/admin/reports/mobile-financial/plan/${serviceId}`, { product_id: productId });
  }

  async getProductCost(productId: string): Promise<{ product_id: string; monthly_cost: number | null }> {
    const response = await this.api.get(`/api/admin/product-costs/${productId}`);
    return response.data;
  }

  async setProductCost(productId: string, monthlyCost: string | null): Promise<void> {
    await this.api.put(`/api/admin/product-costs/${productId}`, { monthly_cost: monthlyCost });
  }

  async getTaskStatusCounts(): Promise<{ counts: Record<string, number> }> {
    const response = await this.api.get('/api/tasks/status-counts');
    return response.data;
  }

  async getTaskMeta(): Promise<TaskMetaResponse> {
    const response = await this.api.get(`/api/tasks/meta`);
    return response.data;
  }

  async listTasks(params?: {
    page?: number;
    per_page?: number;
    status?: string;
    account_id?: string;
    case_id?: string;
    support_desk_ticket_id?: string;
  }): Promise<TaskListResponse> {
    const response = await this.api.get(`/api/tasks`, { params });
    return response.data;
  }

  async getTask(taskId: string): Promise<TaskDetailResponse> {
    const response = await this.api.get(`/api/tasks/${taskId}`);
    return response.data;
  }

  async createTask(body: TaskCreateRequest): Promise<TaskDetailResponse> {
    const response = await this.api.post(`/api/tasks`, body);
    return response.data;
  }

  async updateTask(taskId: string, body: TaskUpdateRequest): Promise<TaskDetailResponse> {
    const response = await this.api.patch(`/api/tasks/${taskId}`, body);
    return response.data;
  }

  async addTaskComment(taskId: string, body: { body: string }): Promise<{ ok: boolean; activity_id: string }> {
    const response = await this.api.post(`/api/tasks/${taskId}/comments`, body);
    return response.data;
  }

  async getTaskConfigTypes(): Promise<TaskTypeAdmin[]> {
    const response = await this.api.get('/api/task-config/types');
    return response.data;
  }

  async getTaskConfigStatuses(): Promise<TaskStatusAdmin[]> {
    const response = await this.api.get('/api/task-config/statuses');
    return response.data;
  }

  async getTaskConfigSettings(): Promise<TaskModuleSettingsOut> {
    const response = await this.api.get('/api/task-config/settings');
    return response.data;
  }

  async patchTaskConfigSettings(body: TaskModuleSettingsUpdate): Promise<TaskModuleSettingsOut> {
    const response = await this.api.patch('/api/task-config/settings', body);
    return response.data;
  }

  async createTaskConfigType(body: { code: string; name: string; sort_order?: number }): Promise<TaskTypeAdmin> {
    const response = await this.api.post('/api/task-config/types', body);
    return response.data;
  }

  async patchTaskConfigType(
    typeId: string,
    body: { name?: string; sort_order?: number; is_active?: boolean; code?: string }
  ): Promise<TaskTypeAdmin> {
    const response = await this.api.patch(`/api/task-config/types/${typeId}`, body);
    return response.data;
  }

  async createTaskConfigStatus(body: {
    code: string;
    name: string;
    sort_order?: number;
    is_closed?: boolean;
    is_positive_completion?: boolean;
  }): Promise<TaskStatusAdmin> {
    const response = await this.api.post('/api/task-config/statuses', body);
    return response.data;
  }

  async patchTaskConfigStatus(
    statusId: string,
    body: {
      name?: string;
      sort_order?: number;
      is_active?: boolean;
      is_closed?: boolean;
      is_positive_completion?: boolean;
      code?: string;
    }
  ): Promise<TaskStatusAdmin> {
    const response = await this.api.patch(`/api/task-config/statuses/${statusId}`, body);
    return response.data;
  }

  async getAccountContacts(accountId: string): Promise<{ contacts: any[]; total: number }> {
    const response = await this.api.get(`/api/accounts/${accountId}/contacts`);
    return response.data;
  }

  async createAccountContact(accountId: string, contact: any): Promise<any> {
    const response = await this.api.post(`/api/accounts/${accountId}/contacts`, contact);
    return response.data;
  }

  async updateAccountContact(accountId: string, contactId: string, contact: any): Promise<any> {
    const response = await this.api.put(`/api/accounts/${accountId}/contacts/${contactId}`, contact);
    return response.data;
  }

  async deleteAccountContact(accountId: string, contactId: string): Promise<void> {
    await this.api.delete(`/api/accounts/${accountId}/contacts/${contactId}`);
  }

  async getAccountAssets(accountId: string): Promise<{ assets: AccountAsset[]; total: number }> {
    const response = await this.api.get(`/api/accounts/${accountId}/assets`);
    return response.data;
  }

  async createAccountAsset(accountId: string, data: AccountAssetCreate): Promise<AccountAsset> {
    const response = await this.api.post(`/api/accounts/${accountId}/assets`, data);
    return response.data;
  }

  async updateAccountAsset(accountId: string, assetId: string, data: AccountAssetUpdate): Promise<AccountAsset> {
    const response = await this.api.put(`/api/accounts/${accountId}/assets/${assetId}`, data);
    return response.data;
  }

  async deleteAccountAsset(accountId: string, assetId: string): Promise<void> {
    await this.api.delete(`/api/accounts/${accountId}/assets/${assetId}`);
  }

  async transferAccountAsset(
    sourceAccountId: string,
    assetId: string,
    targetAccountId: string
  ): Promise<AccountAsset> {
    const response = await this.api.post(
      `/api/accounts/${sourceAccountId}/assets/${assetId}/transfer`,
      { target_account_id: targetAccountId }
    );
    return response.data;
  }

  async searchServices(params?: Record<string, any>): Promise<any> {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, String(value));
        }
      });
    }
    const response = await this.api.get(`/api/accounts/services/search?${queryParams}`);
    return response.data;
  }

  // Cases API
  async getCases(params?: Record<string, any>): Promise<{ cases: any[]; total: number; page: number; per_page: number; pages: number }> {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, String(value));
        }
      });
    }
    const response = await this.api.get(`/api/cases/?${queryParams}`);
    return response.data;
  }

  async getCaseStatusCounts(): Promise<{ counts: Record<string, number> }> {
    const response = await this.api.get('/api/cases/status-counts');
    return response.data;
  }

  async getCasesByAccount(accountId: string, params?: Record<string, any>): Promise<{ cases: any[]; total: number; page: number; per_page: number; pages: number }> {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, String(value));
        }
      });
    }
    const response = await this.api.get(`/api/cases/by-account/${accountId}?${queryParams}`);
    return response.data;
  }

  async getCase(caseId: string): Promise<any> {
    const response = await this.api.get(`/api/cases/${caseId}`);
    return response.data;
  }

  async createCase(data: any): Promise<any> {
    const response = await this.api.post('/api/cases/', data);
    return response.data;
  }

  async updateCase(caseId: string, data: any): Promise<any> {
    const response = await this.api.put(`/api/cases/${caseId}`, data);
    return response.data;
  }

  async reopenCase(caseId: string): Promise<any> {
    const response = await this.api.post(`/api/cases/${caseId}/reopen`);
    return response.data;
  }

  async mergeCase(primaryCaseId: string, secondaryCaseId: string): Promise<any> {
    const response = await this.api.post(`/api/cases/${primaryCaseId}/merge`, { secondary_case_id: secondaryCaseId });
    return response.data;
  }

  async getOpenCasesForDashboard(limit?: number): Promise<{ cases: any[] }> {
    const q = limit != null ? `?limit=${limit}` : '';
    const response = await this.api.get(`/api/cases/open-for-dashboard${q}`);
    return response.data;
  }

  async addCaseNote(caseId: string, content: string): Promise<any> {
    const response = await this.api.post(`/api/cases/${caseId}/notes`, { activity_type: 'note', content });
    return response.data;
  }

  async deleteCaseActivity(caseId: string, activityId: string): Promise<void> {
    await this.api.delete(`/api/cases/${caseId}/activity/${activityId}`);
  }

  // In-app notifications (bell icon)
  async getNotifications(limit?: number, unreadOnly?: boolean): Promise<{
    id: string;
    type: string;
    title: string;
    body: string | null;
    case_id: string | null;
    support_desk_ticket_id?: string | null;
    task_id?: string | null;
    order_id?: string | null;
    read_at: string | null;
    created_at: string;
  }[]> {
    const params: Record<string, number | boolean> = {};
    if (limit != null) params.limit = limit;
    if (unreadOnly != null) params.unread_only = unreadOnly;
    const response = await this.api.get('/api/notifications/', { params });
    return response.data;
  }

  async getNotificationsUnreadCount(): Promise<{ count: number }> {
    const response = await this.api.get('/api/notifications/unread-count');
    return response.data;
  }

  async markNotificationRead(notificationId: string, read: boolean = true): Promise<void> {
    await this.api.patch(`/api/notifications/${notificationId}`, { read });
  }

  async markAllNotificationsRead(): Promise<void> {
    await this.api.post('/api/notifications/mark-all-read');
  }

  async clearNotifications(): Promise<void> {
    await this.api.post('/api/notifications/clear');
  }

  async sendCaseEmail(caseId: string, data: { template_id?: string; to_email: string; cc_email?: string; bcc_email?: string; subject: string; body: string; body_html?: string; variables?: Record<string, string> }): Promise<any> {
    const response = await this.api.post(`/api/cases/${caseId}/email`, data);
    return response.data;
  }

  async uploadCaseAttachment(caseId: string, file: File): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await this.api.post(`/api/cases/${caseId}/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  async deleteCaseAttachment(caseId: string, attachmentId: string): Promise<void> {
    await this.api.delete(`/api/cases/${caseId}/attachments/${attachmentId}`);
  }

  async getCaseTemplates(activeOnly?: boolean): Promise<{ templates: any[]; total: number }> {
    const q = activeOnly !== undefined ? `?active_only=${activeOnly}` : '';
    const response = await this.api.get(`/api/cases/templates/${q}`);
    return response.data;
  }

  async createCaseTemplate(data: { name: string; subject?: string; body: string; variables?: string[]; from_email?: string; from_name?: string; is_active?: boolean }): Promise<any> {
    const response = await this.api.post('/api/cases/templates/', data);
    return response.data;
  }

  async updateCaseTemplate(templateId: string, data: any): Promise<any> {
    const response = await this.api.put(`/api/cases/templates/${templateId}`, data);
    return response.data;
  }

  async deleteCaseTemplate(templateId: string): Promise<void> {
    await this.api.delete(`/api/cases/templates/${templateId}`);
  }

  // Case mailbox (Settings → Case Module → Mailboxes)
  async getCaseMailboxConfig(): Promise<{
    id: string;
    imap_server: string;
    imap_port: number;
    imap_use_ssl: boolean;
    username: string;
    password_set: boolean;
    is_enabled: boolean;
    poll_interval_seconds: number;
    last_polled_at?: string | null;
  }> {
    const response = await this.api.get('/api/case-mailbox/config');
    return response.data;
  }

  async updateCaseMailboxConfig(data: {
    imap_server?: string;
    imap_port?: number;
    imap_use_ssl?: boolean;
    username?: string;
    password?: string;
    is_enabled?: boolean;
    poll_interval_seconds?: number;
  }): Promise<any> {
    const response = await this.api.put('/api/case-mailbox/config', data);
    return response.data;
  }




  // Support Desk mailbox (Settings → Support Desk Module → Mailboxes)
  async getSupportDeskMailboxConfig(): Promise<{
    id: string;
    imap_server: string;
    imap_port: number;
    imap_use_ssl: boolean;
    username: string;
    password_set: boolean;
    auth_method: string;
    microsoft_oauth_connected: boolean;
    is_enabled: boolean;
    poll_interval_seconds: number;
    last_polled_at?: string | null;
  }> {
    const response = await this.api.get('/api/support-desk-mailbox/config');
    return response.data;
  }

  async updateSupportDeskMailboxConfig(data: {
    imap_server?: string;
    imap_port?: number;
    imap_use_ssl?: boolean;
    username?: string;
    password?: string;
    is_enabled?: boolean;
    poll_interval_seconds?: number;
    clear_microsoft_oauth?: boolean;
  }): Promise<any> {
    const response = await this.api.put('/api/support-desk-mailbox/config', data);
    return response.data;
  }

  async getSupportDeskMailboxMicrosoftOAuthUrl(): Promise<{ authorization_url: string; state: string }> {
    const response = await this.api.get('/api/support-desk-mailbox/oauth/microsoft/authorize-url');
    return response.data;
  }

  async getSupportDeskFieldOptionsGrouped(): Promise<Record<string, { id: string; field_name: string; value: string; label: string; sort_order: number; color?: string }[]>> {
    const response = await this.api.get('/api/support-desk/field-options/grouped');
    return response.data;
  }

  async createSupportDeskFieldOption(data: { field_name: string; value: string; label: string; sort_order?: number; color?: string }): Promise<any> {
    const response = await this.api.post('/api/support-desk/field-options/', data);
    return response.data;
  }

  async patchSupportDeskFieldOption(id: string, data: { label?: string; sort_order?: number; color?: string }): Promise<any> {
    const response = await this.api.patch(`/api/support-desk/field-options/${id}`, data);
    return response.data;
  }

  async deleteSupportDeskFieldOption(id: string): Promise<void> {
    await this.api.delete(`/api/support-desk/field-options/${id}`);
  }


  async getSupportDeskTemplates(activeOnly?: boolean): Promise<{ templates: any[]; total: number }> {
    const response = await this.api.get('/api/support-desk/templates/', { params: { active_only: activeOnly } });
    return response.data;
  }

  async createSupportDeskTemplate(data: any): Promise<any> {
    const response = await this.api.post('/api/support-desk/templates/', data);
    return response.data;
  }

  async updateSupportDeskTemplate(id: string, data: any): Promise<any> {
    const response = await this.api.put(`/api/support-desk/templates/${id}`, data);
    return response.data;
  }

  async deleteSupportDeskTemplate(id: string): Promise<void> {
    await this.api.delete(`/api/support-desk/templates/${id}`);
  }

  async getSupportDeskSlaPolicies(): Promise<{ policies: any[]; total: number }> {
    const response = await this.api.get('/api/support-desk/sla-policies/');
    return response.data;
  }

  async createSupportDeskSlaPolicy(data: any): Promise<any> {
    const response = await this.api.post('/api/support-desk/sla-policies/', data);
    return response.data;
  }

  async updateSupportDeskSlaPolicy(id: string, data: any): Promise<any> {
    const response = await this.api.put(`/api/support-desk/sla-policies/${id}`, data);
    return response.data;
  }

  async deleteSupportDeskSlaPolicy(id: string): Promise<void> {
    await this.api.delete(`/api/support-desk/sla-policies/${id}`);
  }

  async getSupportDeskAutomationRules(): Promise<{ rules: any[]; total: number }> {
    const response = await this.api.get('/api/support-desk/automation-rules/');
    return response.data;
  }

  async createSupportDeskAutomationRule(data: any): Promise<any> {
    const response = await this.api.post('/api/support-desk/automation-rules/', data);
    return response.data;
  }

  async updateSupportDeskAutomationRule(id: string, data: any): Promise<any> {
    const response = await this.api.put(`/api/support-desk/automation-rules/${id}`, data);
    return response.data;
  }

  async deleteSupportDeskAutomationRule(id: string): Promise<void> {
    await this.api.delete(`/api/support-desk/automation-rules/${id}`);
  }

  async getSupportDeskTickets(params: Record<string, any>): Promise<{ tickets: any[]; total: number; page: number; per_page: number; pages: number }> {
    const response = await this.api.get('/api/support-desk/tickets/', { params });
    return response.data;
  }

  async getSupportDeskTicketStatusCounts(): Promise<{ counts: Record<string, number> }> {
    const response = await this.api.get('/api/support-desk/tickets/status-counts');
    return response.data;
  }

  async getSupportDeskTicket(
    id: string,
    opts?: { includeActivity?: boolean; includeAttachments?: boolean }
  ): Promise<any> {
    const response = await this.api.get(`/api/support-desk/tickets/${id}`, {
      params: {
        include_activity: opts?.includeActivity,
        include_attachments: opts?.includeAttachments,
      },
    });
    return response.data;
  }

  async markSupportDeskTicketSeen(id: string): Promise<void> {
    await this.api.post(`/api/support-desk/tickets/${id}/seen`);
  }

  async getSupportDeskTicketWorkflow(ticketId: string): Promise<{
    id: string;
    case_id?: string | null;
    support_desk_ticket_id?: string | null;
    template_id: string;
    template_name: string;
    started_at?: string;
    completed_at?: string;
    progress_percent: number;
    steps: {
      id: string;
      step_name: string;
      step_description?: string;
      sort_order: number;
      status: string;
      completed_at?: string;
      notes?: string;
    }[];
  }> {
    const response = await this.api.get(`/api/support-desk/tickets/${ticketId}/workflow`);
    return response.data;
  }

  async applySupportDeskTicketWorkflow(ticketId: string, templateId: string): Promise<any> {
    const response = await this.api.post(`/api/support-desk/tickets/${ticketId}/workflow`, {
      template_id: templateId,
    });
    return response.data;
  }

  async updateSupportDeskTicketWorkflowStep(
    ticketId: string,
    stepInstanceId: string,
    data: { status?: string; notes?: string }
  ): Promise<any> {
    const response = await this.api.patch(
      `/api/support-desk/tickets/${ticketId}/workflow/steps/${stepInstanceId}`,
      data
    );
    return response.data;
  }

  async completeSupportDeskTicketWorkflow(ticketId: string): Promise<any> {
    const response = await this.api.post(`/api/support-desk/tickets/${ticketId}/workflow/complete`, {});
    return response.data;
  }

  async sendSupportDeskTicketEmail(
    ticketId: string,
    data: {
      template_id?: string;
      to_email: string;
      cc_email?: string;
      bcc_email?: string;
      subject: string;
      body: string;
      body_html?: string;
      variables?: Record<string, string>;
    }
  ): Promise<any> {
    const response = await this.api.post(`/api/support-desk/tickets/${ticketId}/email`, data);
    return response.data;
  }

  async uploadSupportDeskTicketAttachment(ticketId: string, file: File): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await this.api.post(`/api/support-desk/tickets/${ticketId}/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  async deleteSupportDeskTicketAttachment(ticketId: string, attachmentId: string): Promise<void> {
    await this.api.delete(`/api/support-desk/tickets/${ticketId}/attachments/${attachmentId}`);
  }

  async createSupportDeskTicket(data: any): Promise<any> {
    const response = await this.api.post('/api/support-desk/tickets/', data);
    return response.data;
  }

  async updateSupportDeskTicket(id: string, data: any): Promise<any> {
    const response = await this.api.patch(`/api/support-desk/tickets/${id}`, data);
    return response.data;
  }

  async bulkSupportDeskTicketsStatus(ticketIds: string[], status: string): Promise<{ ok: boolean; updated: number }> {
    const response = await this.api.post('/api/support-desk/tickets/bulk-status', {
      ticket_ids: ticketIds,
      status,
    });
    return response.data;
  }

  async bulkSupportDeskTicketsDelete(ticketIds: string[]): Promise<{ ok: boolean; deleted: number }> {
    const response = await this.api.post('/api/support-desk/tickets/bulk-delete', {
      ticket_ids: ticketIds,
    });
    return response.data;
  }

  async mergeSupportDeskTickets(body: {
    ticket_ids: string[];
    parent_ticket_id: string;
    comment?: string | null;
  }): Promise<any> {
    const response = await this.api.post('/api/support-desk/tickets/merge', body);
    return response.data;
  }

  async addSupportDeskTicketActivity(id: string, body: { activity_type?: string; content?: string; metadata?: Record<string, unknown> }): Promise<any> {
    const response = await this.api.post(`/api/support-desk/tickets/${id}/activity`, body);
    return response.data;
  }
  // Case field options (Settings → Case Module → Case Field Options)
  async getCaseFieldOptionsGrouped(): Promise<{
    status: { value: string; label: string; sort_order: number; color?: string }[];
    case_reason: { value: string; label: string; sort_order: number; color?: string }[];
    priority: { value: string; label: string; sort_order: number; color?: string }[];
    case_origin: { value: string; label: string; sort_order: number; color?: string }[];
  }> {
    const response = await this.api.get('/api/case-field-options');
    return response.data;
  }

  async getCaseFieldOptionsByField(fieldName: string): Promise<{ id: string; field_name: string; value: string; label: string; sort_order: number; color?: string }[]> {
    const response = await this.api.get(`/api/case-field-options/by-field?field_name=${encodeURIComponent(fieldName)}`);
    return response.data;
  }

  async replaceCaseFieldOptions(fieldName: string, options: { value: string; label: string; sort_order: number; color?: string }[]): Promise<{ id: string; field_name: string; value: string; label: string; sort_order: number; color?: string }[]> {
    const response = await this.api.put(`/api/case-field-options/fields/${encodeURIComponent(fieldName)}`, { options });
    return response.data;
  }

  // Asset field options (Settings → Assets Module)
  async getAssetFieldOptionsGrouped(): Promise<{
    asset_type: { value: string; label: string; sort_order: number }[];
    manufacturer: { value: string; label: string; sort_order: number }[];
    status: { value: string; label: string; sort_order: number }[];
    distributor: { value: string; label: string; sort_order: number }[];
  }> {
    const response = await this.api.get('/api/asset-field-options');
    return response.data;
  }

  async getAssetFieldOptionsByField(fieldName: string): Promise<{ id: string; field_name: string; value: string; label: string; sort_order: number }[]> {
    const response = await this.api.get(`/api/asset-field-options/by-field?field_name=${encodeURIComponent(fieldName)}`);
    return response.data;
  }

  async replaceAssetFieldOptions(fieldName: string, options: { value: string; label: string; sort_order: number }[]): Promise<{ id: string; field_name: string; value: string; label: string; sort_order: number }[]> {
    const response = await this.api.put(`/api/asset-field-options/fields/${encodeURIComponent(fieldName)}`, { options });
    return response.data;
  }

  // Case workflow templates (Settings → Case Module → Workflows)
  async listCaseWorkflowTemplates(activeOnly?: boolean): Promise<{ id: string; name: string; description?: string; is_active: boolean; step_count?: number }[]> {
    const q = activeOnly !== undefined ? `?active_only=${activeOnly}` : '';
    const response = await this.api.get(`/api/case-workflows/templates${q}`);
    return response.data;
  }

  async getCaseWorkflowTemplate(templateId: string): Promise<{ id: string; name: string; description?: string; is_active: boolean; steps?: { id: string; name: string; description?: string; sort_order: number }[] }> {
    const response = await this.api.get(`/api/case-workflows/templates/${templateId}`);
    return response.data;
  }

  async createCaseWorkflowTemplate(data: { name: string; description?: string; is_active?: boolean }): Promise<any> {
    const response = await this.api.post('/api/case-workflows/templates', data);
    return response.data;
  }

  async updateCaseWorkflowTemplate(templateId: string, data: { name?: string; description?: string; is_active?: boolean }): Promise<any> {
    const response = await this.api.put(`/api/case-workflows/templates/${templateId}`, data);
    return response.data;
  }

  async deleteCaseWorkflowTemplate(templateId: string): Promise<void> {
    await this.api.delete(`/api/case-workflows/templates/${templateId}`);
  }

  async getCaseWorkflowTemplateSteps(templateId: string): Promise<{ id: string; template_id: string; name: string; description?: string; sort_order: number }[]> {
    const response = await this.api.get(`/api/case-workflows/templates/${templateId}/steps`);
    return response.data;
  }

  async replaceCaseWorkflowTemplateSteps(templateId: string, steps: { name: string; description?: string; sort_order: number }[]): Promise<any[]> {
    const response = await this.api.put(`/api/case-workflows/templates/${templateId}/steps`, { steps });
    return response.data;
  }

  // Case workflow (per case)
  async getCaseWorkflow(caseId: string): Promise<{ id: string; case_id: string; template_id: string; template_name: string; started_at: string; completed_at?: string; progress_percent: number; steps: { id: string; step_name: string; step_description?: string; sort_order: number; status: string; completed_at?: string; notes?: string }[] }> {
    const response = await this.api.get(`/api/cases/${caseId}/workflow`);
    return response.data;
  }

  async applyCaseWorkflow(caseId: string, templateId: string): Promise<any> {
    const response = await this.api.post(`/api/cases/${caseId}/workflow`, { template_id: templateId });
    return response.data;
  }

  async updateCaseWorkflowStep(caseId: string, stepInstanceId: string, data: { status?: string; notes?: string }): Promise<any> {
    const response = await this.api.patch(`/api/cases/${caseId}/workflow/steps/${stepInstanceId}`, data);
    return response.data;
  }

  async completeCaseWorkflow(caseId: string): Promise<any> {
    const response = await this.api.post(`/api/cases/${caseId}/workflow/complete`, {});
    return response.data;
  }

  async getUsers(): Promise<{ id: string; username: string; display_name: string }[]> {
    const response = await this.api.get('/api/auth/users');
    return response.data;
  }

  // Profile & Security
  async updateMyEmail(email: string): Promise<any> {
    const response = await this.api.patch('/api/auth/me', { email });
    return response.data;
  }

  async updateMyProfile(profile: { email?: string; display_name?: string }): Promise<any> {
    const response = await this.api.patch('/api/auth/me', profile);
    return response.data;
  }

  async changeMyPassword(currentPassword: string, newPassword: string, totpCode?: string): Promise<any> {
    const payload: any = { current_password: currentPassword, new_password: newPassword };
    if (totpCode) payload.totp_code = totpCode;
    const response = await this.api.post('/api/auth/change-password', payload);
    return response.data;
  }

  // Notification Services
  async getNotificationServices(): Promise<any> {
    const response = await this.api.get('/api/notification-services/');
    return response.data;
  }

  async getNotificationService(serviceId: string): Promise<any> {
    const response = await this.api.get(`/api/notification-services/${serviceId}`);
    return response.data;
  }

  async toggleNotificationService(serviceId: string, isEnabled: boolean): Promise<any> {
    const response = await this.api.patch(`/api/notification-services/${serviceId}/toggle?is_enabled=${isEnabled}`);
    return response.data;
  }

  async testNotificationServiceConnection(serviceId: string): Promise<any> {
    const response = await this.api.post(`/api/notification-services/${serviceId}/test-connection`);
    return response.data;
  }

  async sendTestNotificationMessage(serviceId: string): Promise<any> {
    const response = await this.api.post(`/api/notification-services/${serviceId}/send-test-message`);
    return response.data;
  }

  // User Notifications
  async getMyNotificationPreferences(): Promise<any> {
    const response = await this.api.get('/api/user-notifications/');
    return response.data;
  }

  async createNotificationPreference(preference: any): Promise<any> {
    const response = await this.api.post('/api/user-notifications/', preference);
    return response.data;
  }

  async updateNotificationPreference(preferenceId: string, updates: any): Promise<any> {
    const response = await this.api.patch(`/api/user-notifications/${preferenceId}`, updates);
    return response.data;
  }

  async deleteNotificationPreference(preferenceId: string): Promise<void> {
    await this.api.delete(`/api/user-notifications/${preferenceId}`);
  }

  async validateNotificationPreference(preferenceId: string): Promise<any> {
    const response = await this.api.post(`/api/user-notifications/${preferenceId}/validate`);
    return response.data;
  }

  // ============= Ordering System (Public) =============
  
  async getOrderProducts(params?: {
    category?: string;
    subcategory?: string;
    type?: string;
    customer_type?: string;
  }): Promise<OrderProduct[]> {
    const queryParams = new URLSearchParams();
    if (params?.category) queryParams.append('category', params.category);
    if (params?.subcategory) queryParams.append('subcategory', params.subcategory);
    if (params?.type) queryParams.append('type', params.type);
    if (params?.customer_type) queryParams.append('customer_type', params.customer_type);
    
    const queryString = queryParams.toString();
    const url = `/api/orders/products${queryString ? `?${queryString}` : ''}`;
    const response = await this.api.get(url);
    return response.data;
  }

  async getOrderProduct(productId: string): Promise<OrderProduct> {
    const response = await this.api.get(`/api/orders/products/${productId}`);
    return response.data;
  }

  async getOrderCategories(): Promise<CategoryWithSubcategories[]> {
    const response = await this.api.get('/api/orders/categories');
    return response.data;
  }

  async getEzidebitPaymentTerms(): Promise<{
    credit_card_terms: string;
    direct_debit_terms: string;
  }> {
    const response = await this.api.get('/api/orders/ezidebit-payment-terms');
    return response.data;
  }

  async getOrderHardware(params: {
    category: string;
    subcategory: string;
  }): Promise<OrderHardware[]> {
    const q = new URLSearchParams({ category: params.category, subcategory: params.subcategory });
    const response = await this.api.get(`/api/orders/hardware?${q.toString()}`);
    return response.data;
  }

  async getOrderAddons(params: {
    category: string;
    subcategory: string;
  }): Promise<OrderAddon[]> {
    const q = new URLSearchParams({ category: params.category, subcategory: params.subcategory });
    const response = await this.api.get(`/api/orders/addons?${q.toString()}`);
    return response.data;
  }

  async createDraftOrder(data: { internet_plan_id: string; service_address?: string }): Promise<OrderResponse> {
    const response = await this.api.post('/api/orders/draft', data);
    return response.data;
  }

  async getOrder(orderId: string): Promise<OrderResponse> {
    const response = await this.api.get(`/api/orders/${orderId}`);
    return response.data;
  }

  async updateDraftOrder(
    orderId: string,
    data: {
      internet_plan_id?: string;
      equipment_ids?: string[];
      hardware_ids?: string[];
      voice_service_selected?: boolean;
      voice_plan_id?: string;
      voice_channels?: number;
      service_address?: string;
    }
  ): Promise<OrderResponse> {
    const response = await this.api.patch(`/api/orders/${orderId}`, data);
    return response.data;
  }

  async submitOrder(order: OrderSubmit): Promise<OrderResponse> {
    const response = await this.api.post('/api/orders/submit', order);
    return response.data;
  }

  async validateCoupon(code: string, productIds: string[]): Promise<import('../types/order').CouponValidateResponse> {
    const response = await this.api.post('/api/orders/validate-coupon', { code, product_ids: productIds });
    return response.data;
  }

  // ============= Admin Coupon Management =============

  async listCoupons(): Promise<import('../types/order').CouponResponse[]> {
    const response = await this.api.get('/api/admin/coupons');
    return response.data;
  }

  async createCoupon(data: import('../types/order').CouponCreate): Promise<import('../types/order').CouponResponse> {
    const response = await this.api.post('/api/admin/coupons', data);
    return response.data;
  }

  async getCoupon(couponId: string): Promise<import('../types/order').CouponResponse> {
    const response = await this.api.get(`/api/admin/coupons/${couponId}`);
    return response.data;
  }

  async updateCoupon(couponId: string, data: import('../types/order').CouponUpdate): Promise<import('../types/order').CouponResponse> {
    const response = await this.api.patch(`/api/admin/coupons/${couponId}`, data);
    return response.data;
  }

  async deleteCoupon(couponId: string): Promise<void> {
    await this.api.delete(`/api/admin/coupons/${couponId}`);
  }

  // ============= Admin Ordering System =============

  async adminGetEzidebitPaymentTerms(): Promise<{
    credit_card_terms: string;
    direct_debit_terms: string;
  }> {
    const response = await this.api.get('/api/admin/orders/settings/ezidebit-payment-terms');
    return response.data;
  }

  async adminUpdateEzidebitPaymentTerms(data: {
    credit_card_terms: string;
    direct_debit_terms: string;
  }): Promise<{ credit_card_terms: string; direct_debit_terms: string }> {
    const response = await this.api.put('/api/admin/orders/settings/ezidebit-payment-terms', data);
    return response.data;
  }

  async adminGetOrderConfirmationEmail(): Promise<{
    layout_template_id: string;
    email_subject: string;
    email_body_html: string;
    layout_template_options: { id: string; name: string }[];
  }> {
    const response = await this.api.get('/api/admin/orders/settings/order-confirmation-email');
    return response.data;
  }

  async adminUpdateOrderConfirmationEmail(data: {
    layout_template_id: string;
    email_subject: string;
    email_body_html: string;
  }): Promise<{
    layout_template_id: string;
    email_subject: string;
    email_body_html: string;
    layout_template_options: { id: string; name: string }[];
  }> {
    const response = await this.api.put('/api/admin/orders/settings/order-confirmation-email', data);
    return response.data;
  }
  
  async adminListOrders(params?: {
    status?: string;
    exclude_statuses?: string;
    date_from?: string;
    date_to?: string;
    search?: string;
    page?: number;
    per_page?: number;
    account_id?: string;
    unassigned_only?: boolean;
  }): Promise<any[]> {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append('status', params.status);
    if (params?.exclude_statuses) queryParams.append('exclude_statuses', params.exclude_statuses);
    if (params?.date_from) queryParams.append('date_from', params.date_from);
    if (params?.date_to) queryParams.append('date_to', params.date_to);
    if (params?.search) queryParams.append('search', params.search);
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.per_page) queryParams.append('per_page', params.per_page.toString());
    if (params?.account_id) queryParams.append('account_id', params.account_id);
    if (params?.unassigned_only) queryParams.append('unassigned_only', 'true');

    const queryString = queryParams.toString();
    const url = `/api/admin/orders${queryString ? `?${queryString}` : ''}`;
    const response = await this.api.get(url);
    return response.data;
  }

  async adminCreateStaffOrder(data: {
    lines?: { product_id: string; instances: Record<string, unknown>[] }[];
    product_id?: string;
    account_id?: string | null;
    customer_type: 'residential' | 'business';
    first_name: string;
    last_name: string;
    email: string;
    mobile_number: string;
    service_address: string;
    billing_address?: string;
    organisation_name?: string;
    abn_acn?: string;
  }): Promise<any> {
    const response = await this.api.post('/api/admin/orders/staff', data);
    return response.data;
  }

  /** @deprecated Use adminCreateStaffOrder (same request body; still supported). */
  async adminCreateStaffMobileOrder(data: {
    lines?: { product_id: string; instances: Record<string, unknown>[] }[];
    product_id?: string;
    account_id?: string | null;
    customer_type: 'residential' | 'business';
    first_name: string;
    last_name: string;
    email: string;
    mobile_number: string;
    service_address: string;
    billing_address?: string;
    organisation_name?: string;
    abn_acn?: string;
  }): Promise<any> {
    return this.adminCreateStaffOrder(data);
  }

  async adminViewAccountAsCustomer(accountId: string): Promise<{ view_url: string; expires_at: string }> {
    const response = await this.api.post(`/api/accounts/${accountId}/view-as-customer`);
    return response.data;
  }

  async adminGetOrder(orderId: string): Promise<any> {
    const response = await this.api.get(`/api/admin/orders/${orderId}`);
    return response.data;
  }

  async adminUpdateOrder(orderId: string, data: any): Promise<any> {
    const response = await this.api.patch(`/api/admin/orders/${orderId}`, data);
    return response.data;
  }

  // ---- Hosting servers (WHM) and one-click cPanel login ----
  async getHostingServers(): Promise<any[]> {
    const response = await this.api.get('/api/admin/hosting-servers');
    return response.data;
  }

  async createHostingServer(body: Record<string, unknown>): Promise<any> {
    const response = await this.api.post('/api/admin/hosting-servers', body);
    return response.data;
  }

  async updateHostingServer(id: string, body: Record<string, unknown>): Promise<any> {
    const response = await this.api.put(`/api/admin/hosting-servers/${id}`, body);
    return response.data;
  }

  async deleteHostingServer(id: string): Promise<void> {
    await this.api.delete(`/api/admin/hosting-servers/${id}`);
  }

  async testHostingServer(id: string): Promise<{ ok: boolean; message: string }> {
    const response = await this.api.post(`/api/admin/hosting-servers/${id}/test`);
    return response.data;
  }

  async adminCpanelLogin(serviceId: string): Promise<{ url: string }> {
    const response = await this.api.post(`/api/admin/services/${serviceId}/cpanel-login`);
    return response.data;
  }

  // ---- Account payment methods (Billing > Payment Methods). Methods can be added or deleted, never edited. ----
  async getAccountPaymentMethods(accountId: string): Promise<any[]> {
    const response = await this.api.get(`/api/admin/accounts/${accountId}/payment-methods`);
    return response.data;
  }

  async addAccountPaymentMethod(accountId: string, body: Record<string, unknown>): Promise<any> {
    const response = await this.api.post(`/api/admin/accounts/${accountId}/payment-methods`, body);
    return response.data;
  }

  async revealAccountPaymentMethod(accountId: string, methodId: string): Promise<any> {
    const response = await this.api.post(`/api/admin/accounts/${accountId}/payment-methods/${methodId}/reveal`);
    return response.data;
  }

  async deleteAccountPaymentMethod(accountId: string, methodId: string): Promise<any> {
    const response = await this.api.delete(`/api/admin/accounts/${accountId}/payment-methods/${methodId}`);
    return response.data;
  }

  async deleteAccountPaymentMethodCvv(accountId: string, methodId: string): Promise<any> {
    const response = await this.api.post(`/api/admin/accounts/${accountId}/payment-methods/${methodId}/delete-cvv`);
    return response.data;
  }

  async getAccountPaymentAudit(accountId: string): Promise<any[]> {
    const response = await this.api.get(`/api/admin/accounts/${accountId}/payment-methods/audit`);
    return response.data;
  }

  async adminGetPaymentDetails(orderId: string): Promise<any> {
    const response = await this.api.get(`/api/admin/orders/${orderId}/payment`);
    return response.data;
  }

  async adminDeletePaymentDetails(orderId: string): Promise<any> {
    const response = await this.api.post(`/api/admin/orders/${orderId}/delete-payment`);
    return response.data;
  }

  async adminGetProvisioningSteps(orderId: string): Promise<{ line_items: import('../types/order').LineItemProvisioningState[] }> {
    const response = await this.api.get(`/api/admin/orders/${orderId}/provisioning-steps`);
    return response.data;
  }

  async adminToggleProvisioningStep(
    orderId: string,
    cartLineIndex: number,
    stepKey: string,
  ): Promise<import('../types/order').LineItemProvisioningState> {
    const response = await this.api.post(
      `/api/admin/orders/${orderId}/provisioning-steps/${cartLineIndex}/${stepKey}/toggle`,
    );
    return response.data;
  }

  async adminAssignStaticIp(
    orderId: string,
    cartLineIndex: number,
    payload: { ip_address: string; linked_service_id?: string | null; notes?: string | null },
  ): Promise<import('../types/order').LineItemProvisioningState> {
    const response = await this.api.post(
      `/api/admin/orders/${orderId}/provisioning-steps/${cartLineIndex}/static-ip`,
      payload,
    );
    return response.data;
  }

  async adminGetStaticIpAssignment(
    orderId: string,
    cartLineIndex: number,
  ): Promise<{
    account_service_id: string | null;
    ip_address: string | null;
    notes: string | null;
    linked_service_id: string | null;
    suggested_service_id: string | null;
    linkable_services: { id: string; label: string }[];
  }> {
    const response = await this.api.get(
      `/api/admin/orders/${orderId}/provisioning-steps/${cartLineIndex}/static-ip`,
    );
    return response.data;
  }

  async adminGetRadiusLookup(username: string): Promise<import('../types/radius').RadiusLookupResult> {
    const response = await this.api.get(`/api/admin/radius/lookup/${encodeURIComponent(username)}`);
    return response.data;
  }

  async adminGetRadiusSubscriber(username: string): Promise<{ username: string; connection_type: 'ipoe' | 'pppoe'; groupname: string | null }> {
    const response = await this.api.get(`/api/admin/radius/subscriber/${encodeURIComponent(username)}`);
    return response.data;
  }

  async adminGetTrafficFlow(params: { connectionType: 'ipoe' | 'pppoe'; username: string; assignedIp?: string | null }): Promise<import('../types/radius').TrafficFlowReading> {
    const query = new URLSearchParams({ connection_type: params.connectionType, username: params.username });
    if (params.assignedIp) query.set('assigned_ip', params.assignedIp);
    const response = await this.api.get(`/api/admin/router/traffic-flow?${query.toString()}`);
    return response.data;
  }

  async adminGetRadiusMonthlyUsage(username: string): Promise<{
    upload_gb: number;
    download_gb: number;
    session_count: number;
  }> {
    const response = await this.api.get(`/api/admin/radius/monthly-usage/${encodeURIComponent(username)}`);
    return response.data;
  }

  async adminGetRadiusSessionStatus(username: string): Promise<{
    status: 'online' | 'offline' | 'unknown';
    is_online: boolean;
    assigned_ip?: string | null;
    connected_since?: string | null;
    online_minutes?: number | null;
    upload_mb?: number | null;
    download_mb?: number | null;
    last_update?: string | null;
  }> {
    const response = await this.api.get(`/api/admin/radius/session/${encodeURIComponent(username)}`);
    return response.data;
  }

  async publicSupaServicedBuildings(): Promise<{ status: string; statusCode: string; servicedBuildings: import('../types/supa').SupaSQBuilding[] }> {
    const response = await this.api.get('/api/public/supa/serviced-buildings');
    return response.data;
  }

  async publicSupaServiceQualification(address: {
    StreetName: string;
    Suburb: string;
    UnitNo?: string;
    StreetNo?: string;
    State?: string;
    Postcode?: string;
    SubPremise?: string;
  }): Promise<SupaSQResponse> {
    const response = await this.api.post('/api/public/supa/service-qualification', address);
    return response.data;
  }

  async adminSupaPlanList(): Promise<{
    status: string;
    statusCode: string;
    description: string;
    availablePlans: { planName: string; tariffID: string; serviceType: string; monthlyFee: string; uploadSpeed: string; downloadSpeed: string }[];
  }> {
    const response = await this.api.get('/api/admin/supa/plan-list');
    return response.data;
  }

  async adminSupaServicedBuildings(): Promise<{ status: string; statusCode: string; servicedBuildings: import('../types/supa').SupaSQBuilding[] }> {
    const response = await this.api.post('/api/admin/supa/serviced-buildings');
    return response.data;
  }

  async adminSupaServiceQualification(address: {
    StreetName: string;
    Suburb: string;
    UnitNo?: string;
    StreetNo?: string;
    State?: string;
    Postcode?: string;
    Longitude?: number;
    Latitude?: number;
    PlaceId?: string;
    SubPremise?: string;
  }): Promise<SupaSQResponse> {
    const response = await this.api.post('/api/admin/supa/service-qualification', address);
    return response.data;
  }

  async getSupaSessionStatusSummary(): Promise<{
    online: number;
    offline: number;
    stale: number;
    unknown: number;
    total: number;
  }> {
    const response = await this.api.get('/api/admin/supa/services/session-status');
    return response.data;
  }

  async getNbnSessionStatusSummary(): Promise<{
    online: number;
    offline: number;
    stale: number;
    unknown: number;
    total: number;
    live_status: boolean;
  }> {
    const response = await this.api.get('/api/admin/nbn/services/session-status');
    return response.data;
  }

  async adminSupaProvisionContext(orderId: string, cartLineIndex: number): Promise<{
    first_name: string;
    last_name: string;
    email: string;
    mobile_number: string;
    account_number: string | null;
    service_address: string;
    supa_tariff_id: string | null;
    product_name: string | null;
  }> {
    const response = await this.api.get(`/api/admin/supa/provision-context/${orderId}/${cartLineIndex}`);
    return response.data;
  }

  async adminSupaNewOrder(body: {
    order_id: string;
    cart_line_index: number;
    service_location: string;
    tariff_id: string;
    plan_id?: string;
    activation_date: string;
    customer_first_name: string;
    customer_last_name: string;
    customer_email: string;
    customer_phone: string;
    buid?: string;
  }): Promise<import('../types/order').LineItemProvisioningState> {
    const response = await this.api.post('/api/admin/supa/new-order', body);
    return response.data;
  }

  async adminSupaMarkManualComplete(body: {
    order_id: string;
    cart_line_index: number;
    buid: string;
    supa_order_id?: string;
    service_id?: string;
    reason?: string;
  }): Promise<import('../types/order').LineItemProvisioningState> {
    const response = await this.api.post('/api/admin/supa/manual-complete', body);
    return response.data;
  }

  async adminSupaFulfillOrder(body: {
    order_id: string;
    cart_line_index: number;
  }): Promise<import('../types/order').LineItemProvisioningState> {
    const response = await this.api.post('/api/admin/supa/fulfill-order', body);
    return response.data;
  }

  async getOrderMonthlyStats(): Promise<{ month: string; order_count: number; total_mrr: number }[]> {
    const response = await this.api.get('/api/admin/orders/monthly-stats');
    return response.data;
  }

  async getServicesMap(serviceTypes?: string[]): Promise<{
    service_id: string;
    service_type: string;
    account_id: string;
    account_name: string | null;
    address_formatted: string | null;
    address_line1: string | null;
    address_line2: string | null;
    address_suburb: string | null;
    address_state: string | null;
    address_postcode: string | null;
    session_status: string | null;
    auth_type: string | null;
    assigned_ip: string | null;
    cpe_mac_address: string | null;
    nbn_technology_type: string | null;
  }[]> {
    const params = serviceTypes?.length ? { service_types: serviceTypes.join(',') } : {};
    const response = await this.api.get('/api/admin/reports/services-map', { params });
    return response.data;
  }

  async getMappableServiceTypes(): Promise<string[]> {
    const response = await this.api.get('/api/admin/reports/services-map/types');
    return response.data;
  }

  async adminGetIPoEStatus(macAddress: string, assignedIp?: string | null): Promise<{
    status: 'online' | 'offline';
    cgnat_ip: string | null;
    upload_bytes: number | null;
    download_bytes: number | null;
    hostname: string | null;
    last_seen: string | null;
  }> {
    const params: Record<string, string> = {};
    if (assignedIp) params.assigned_ip = assignedIp;
    const response = await this.api.get(`/api/admin/router/ipoe-status/${encodeURIComponent(macAddress)}`, { params });
    return response.data;
  }

  async adminProvisionRadius(payload: {
    order_id: string;
    cart_line_index: number;
    connection_type: 'ipoe' | 'pppoe';
    username: string;
    password?: string;
    groupname: string;
    existing_username?: string;
  }): Promise<import('../types/order').LineItemProvisioningState> {
    const response = await this.api.post('/api/admin/radius/provision', payload);
    return response.data;
  }

  async adminGetOrderAccountService(
    orderId: string,
    cartLineIndex: number,
  ): Promise<{
    account_service_id: string;
    service_id: string;
    type_specific_data: Record<string, unknown>;
    nbn_plan_id?: string | null;
    wsm_service_id?: string | null;
    nbn_carrier_id?: string | null;
    nbn_avc?: string | null;
    nbn_poi?: string | null;
    nbn_technology_type?: string | null;
    nbn_ip_address_type?: string | null;
    nbn_ip_address?: string | null;
    nbn_service_class?: string | null;
    nbn_sla?: string | null;
    nbn_mapping?: string | null;
    nbn_unid_port?: string | null;
  }> {
    const response = await this.api.get(
      `/api/admin/orders/${orderId}/provisioning-steps/${cartLineIndex}/account-service`,
    );
    return response.data;
  }

  async adminProvisionAccountService(
    orderId: string,
    cartLineIndex: number,
    payload: {
      service_id: string;
      type_specific_data: Record<string, unknown>;
      nbn_plan_id?: string | null;
      wsm_service_id?: string | null;
      nbn_carrier_id?: string | null;
      nbn_avc?: string | null;
      nbn_poi?: string | null;
      nbn_technology_type?: string | null;
      nbn_ip_address_type?: string | null;
      nbn_ip_address?: string | null;
      nbn_service_class?: string | null;
      nbn_sla?: string | null;
      nbn_mapping?: string | null;
      nbn_unid_port?: string | null;
    },
  ): Promise<import('../types/order').LineItemProvisioningState> {
    const response = await this.api.post(
      `/api/admin/orders/${orderId}/provisioning-steps/${cartLineIndex}/account-service`,
      payload,
    );
    return response.data;
  }

  async adminGetOrderActivityLog(
    orderId: string,
    types?: string[],
  ): Promise<{ entries: import('../types/order').ActivityLogEntry[] }> {
    const params: Record<string, string> = {};
    if (types && types.length > 0) params.types = types.join(',');
    const response = await this.api.get(`/api/admin/orders/${orderId}/activity-log`, { params });
    return response.data;
  }

  async adminGetPortingDocument(orderId: string): Promise<Blob> {
    const response = await this.api.get(`/api/admin/orders/${orderId}/document`, {
      responseType: 'blob',
    });
    return response.data;
  }

  async adminUploadOrderItemPortingDocument(
    orderId: string,
    cartLineIndex: number,
    instanceIndex: number,
    file: File,
  ): Promise<void> {
    const fd = new FormData();
    fd.append('cart_line_index', String(cartLineIndex));
    fd.append('instance_index', String(instanceIndex));
    fd.append('file', file);
    await this.api.post(`/api/admin/orders/${orderId}/items/porting-document`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  async adminListHardware(categoryId?: string): Promise<any[]> {
    const url = categoryId ? `/api/admin/hardware?category_id=${categoryId}` : '/api/admin/hardware';
    const response = await this.api.get(url);
    return response.data;
  }

  async adminGetHardware(hardwareId: string): Promise<any> {
    const response = await this.api.get(`/api/admin/hardware/${hardwareId}`);
    return response.data;
  }

  async adminCreateHardware(data: any): Promise<any> {
    const response = await this.api.post('/api/admin/hardware', data);
    return response.data;
  }

  async adminUpdateHardware(hardwareId: string, data: any): Promise<any> {
    const response = await this.api.put(`/api/admin/hardware/${hardwareId}`, data);
    return response.data;
  }

  async adminDeleteHardware(hardwareId: string): Promise<void> {
    await this.api.delete(`/api/admin/hardware/${hardwareId}`);
  }

  async adminListAddons(categoryId?: string): Promise<any[]> {
    const url = categoryId ? `/api/admin/addons?category_id=${categoryId}` : '/api/admin/addons';
    const response = await this.api.get(url);
    return response.data;
  }

  async adminGetAddon(addonId: string): Promise<any> {
    const response = await this.api.get(`/api/admin/addons/${addonId}`);
    return response.data;
  }

  async adminCreateAddon(data: any): Promise<any> {
    const response = await this.api.post('/api/admin/addons', data);
    return response.data;
  }

  async adminUpdateAddon(addonId: string, data: any): Promise<any> {
    const response = await this.api.put(`/api/admin/addons/${addonId}`, data);
    return response.data;
  }

  async adminDeleteAddon(addonId: string): Promise<void> {
    await this.api.delete(`/api/admin/addons/${addonId}`);
  }

  async adminUploadHardwareImage(hardwareId: string, file: File): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await this.api.post(`/api/admin/hardware/${hardwareId}/image`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  async adminGetCategoryHardwareFields(categoryId: string): Promise<SubcategoryProductFieldItem[]> {
    const response = await this.api.get(`/api/admin/categories/${categoryId}/hardware-fields`);
    return response.data;
  }

  async adminUpdateCategoryHardwareFields(categoryId: string, fields: SubcategoryProductFieldItem[]): Promise<any> {
    const response = await this.api.put(`/api/admin/categories/${categoryId}/hardware-fields`, { fields });
    return response.data;
  }

  async adminListUpstreamCarriers(activeOnly: boolean = false): Promise<UpstreamCarrier[]> {
    const params = activeOnly ? '?active_only=true' : '';
    const response = await this.api.get(`/api/admin/categories/upstream-carriers${params}`);
    return response.data;
  }

  async adminCreateUpstreamCarrier(data: { name: string; display_order?: number; is_active?: boolean }): Promise<UpstreamCarrier> {
    const response = await this.api.post('/api/admin/categories/upstream-carriers', data);
    return response.data;
  }

  async adminUpdateUpstreamCarrier(
    carrierId: string,
    data: { name?: string; display_order?: number; is_active?: boolean }
  ): Promise<UpstreamCarrier> {
    const response = await this.api.put(`/api/admin/categories/upstream-carriers/${carrierId}`, data);
    return response.data;
  }

  async adminDeleteUpstreamCarrier(carrierId: string): Promise<void> {
    await this.api.delete(`/api/admin/categories/upstream-carriers/${carrierId}`);
  }

  async adminListProducts(): Promise<OrderProduct[]> {
    const response = await this.api.get('/api/admin/products');
    return response.data;
  }

  async adminGetProduct(productId: string): Promise<OrderProduct> {
    const response = await this.api.get(`/api/admin/products/${productId}`);
    return response.data;
  }

  async adminCreateProduct(product: any): Promise<OrderProduct> {
    const response = await this.api.post('/api/admin/products', product);
    return response.data;
  }

  async adminUpdateProduct(productId: string, product: any): Promise<OrderProduct> {
    const response = await this.api.put(`/api/admin/products/${productId}`, product);
    return response.data;
  }

  async adminDeleteProduct(productId: string): Promise<void> {
    await this.api.delete(`/api/admin/products/${productId}`);
  }

  async adminUploadCriticalInfoSummary(productId: string, file: File): Promise<OrderProduct> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await this.api.post(`/api/admin/products/${productId}/critical-info-summary`, formData);
    return response.data;
  }

  async adminUploadProductImage(productId: string, file: File): Promise<OrderProduct> {
    const form = new FormData();
    form.append('file', file);
    const response = await this.api.post(`/api/admin/products/${productId}/image`, form);
    return response.data;
  }

  async adminDeleteProductImage(productId: string): Promise<OrderProduct> {
    const response = await this.api.delete(`/api/admin/products/${productId}/image`);
    return response.data;
  }

  async adminListCategories(): Promise<CategoryWithSubcategories[]> {
    const response = await this.api.get('/api/admin/categories');
    return response.data;
  }

  async adminGetCategory(categoryId: string): Promise<CategoryWithSubcategories> {
    const response = await this.api.get(`/api/admin/categories/${categoryId}`);
    return response.data;
  }

  async adminCreateCategory(category: any): Promise<CategoryWithSubcategories> {
    const response = await this.api.post('/api/admin/categories', category);
    return response.data;
  }

  async adminUpdateCategory(categoryId: string, category: any): Promise<CategoryWithSubcategories> {
    const response = await this.api.put(`/api/admin/categories/${categoryId}`, category);
    return response.data;
  }

  async adminDeleteCategory(categoryId: string): Promise<void> {
    await this.api.delete(`/api/admin/categories/${categoryId}`);
  }

  async adminListSubcategories(categoryId?: string): Promise<OrderSubcategory[]> {
    const queryParams = new URLSearchParams();
    if (categoryId) queryParams.append('category_id', categoryId);
    const queryString = queryParams.toString();
    const url = `/api/admin/categories/subcategories${queryString ? `?${queryString}` : ''}`;
    const response = await this.api.get(url);
    return response.data;
  }

  async adminGetSubcategory(subcategoryId: string): Promise<OrderSubcategory> {
    const response = await this.api.get(`/api/admin/categories/subcategories/${subcategoryId}`);
    return response.data;
  }

  async adminCreateSubcategory(subcategory: any): Promise<OrderSubcategory> {
    const response = await this.api.post('/api/admin/categories/subcategories', subcategory);
    return response.data;
  }

  async adminUpdateSubcategory(subcategoryId: string, subcategory: any): Promise<OrderSubcategory> {
    const response = await this.api.put(`/api/admin/categories/subcategories/${subcategoryId}`, subcategory);
    return response.data;
  }

  async adminDeleteSubcategory(subcategoryId: string): Promise<void> {
    await this.api.delete(`/api/admin/categories/subcategories/${subcategoryId}`);
  }

  async adminUploadSubcategoryTerms(subcategoryId: string, file: File, termsLabel?: string): Promise<OrderSubcategory> {
    const form = new FormData();
    form.append('file', file);
    if (termsLabel && termsLabel.trim()) form.append('terms_label', termsLabel.trim());
    const response = await this.api.post(`/api/admin/categories/subcategories/${subcategoryId}/terms`, form);
    return response.data;
  }

  async adminDeleteSubcategoryTerms(subcategoryId: string): Promise<OrderSubcategory> {
    const response = await this.api.delete(`/api/admin/categories/subcategories/${subcategoryId}/terms`);
    return response.data;
  }

  async adminGetApprovalLinkSettings(): Promise<{ ttl_days: number; customer_portal_base_url: string }> {
    const response = await this.api.get('/api/admin/orders/settings/approval-link');
    return response.data;
  }

  async adminPutApprovalLinkSettings(body: {
    ttl_days: number;
    customer_portal_base_url: string;
  }): Promise<{ ttl_days: number; customer_portal_base_url: string }> {
    const response = await this.api.put('/api/admin/orders/settings/approval-link', body);
    return response.data;
  }

  async adminOrderRelinkCommunications(orderId: string): Promise<void> {
    await this.api.post(`/api/admin/orders/${orderId}/relink-communications`);
  }

  async adminMintOrderApprovalLink(orderId: string): Promise<{
    token: string;
    expires_at: string;
    review_url_path: string;
    customer_review_url: string;
  }> {
    const response = await this.api.post(`/api/admin/orders/${orderId}/approval-link`);
    return response.data;
  }

  async adminGetOrderApprovalEmail(): Promise<{ email_subject: string; email_body_html: string }> {
    const response = await this.api.get('/api/admin/orders/settings/order-approval-email');
    return response.data;
  }

  async adminPutOrderApprovalEmail(body: {
    email_subject: string;
    email_body_html: string;
  }): Promise<{ email_subject: string; email_body_html: string }> {
    const response = await this.api.put('/api/admin/orders/settings/order-approval-email', body);
    return response.data;
  }

  async adminSendOrderApprovalEmail(
    orderId: string,
    body: { approval_token: string },
  ): Promise<{ sent: boolean }> {
    const response = await this.api.post(`/api/admin/orders/${orderId}/approval-email`, body);
    return response.data;
  }

  async adminGetSubcategoryProductFields(subcategoryId: string): Promise<SubcategoryProductField[]> {
    const response = await this.api.get(`/api/admin/categories/subcategories/${subcategoryId}/product-fields`);
    return response.data;
  }

  async adminGetCategoryProductFields(categoryId: string): Promise<SubcategoryProductField[]> {
    const response = await this.api.get(`/api/admin/categories/${categoryId}/product-fields`);
    return response.data;
  }

  async adminUpdateCategoryProductFields(
    categoryId: string,
    fields: SubcategoryProductFieldItem[]
  ): Promise<SubcategoryProductField[]> {
    const response = await this.api.put(`/api/admin/categories/${categoryId}/product-fields`, { fields });
    return response.data;
  }

  async adminResolveProductFieldDefinitions(
    categoryId: string,
    subcategoryId?: string | null
  ): Promise<ProductFieldDefinition[]> {
    const q = new URLSearchParams({ category_id: categoryId });
    if (subcategoryId) q.set('subcategory_id', subcategoryId);
    const response = await this.api.get(`/api/admin/categories/resolve-product-field-definitions?${q.toString()}`);
    return response.data;
  }

  async adminUpdateSubcategoryProductFields(
    subcategoryId: string,
    fields: SubcategoryProductFieldItem[]
  ): Promise<SubcategoryProductField[]> {
    const response = await this.api.put(`/api/admin/categories/subcategories/${subcategoryId}/product-fields`, {
      fields,
    });
    return response.data;
  }

  async getOrderProductFields(params: {
    category: string;
    subcategory?: string;
  }): Promise<ProductFieldDefinition[]> {
    const q = new URLSearchParams({ category: params.category });
    if (params.subcategory) q.set('subcategory', params.subcategory);
    const response = await this.api.get(`/api/orders/product-fields?${q.toString()}`);
    return response.data;
  }

  async adminGetOrderSignedPdfEmail(): Promise<{ email_subject: string; email_body_html: string; email_cc: string }> {
    const response = await this.api.get('/api/admin/orders/settings/order-signed-pdf-email');
    return response.data;
  }

  async adminPutOrderSignedPdfEmail(body: {
    email_subject: string;
    email_body_html: string;
    email_cc: string;
  }): Promise<{ email_subject: string; email_body_html: string; email_cc: string }> {
    const response = await this.api.put('/api/admin/orders/settings/order-signed-pdf-email', body);
    return response.data;
  }

  async getAccountCommunications(accountId: string, page = 1, perPage = 25): Promise<{
    entries: {
      id: string;
      to_email: string;
      subject: string;
      body_html: string | null;
      email_type: string;
      reference_label: string | null;
      sent_at: string;
      sent_by_user_id: string | null;
    }[];
    total: number;
    page: number;
    per_page: number;
  }> {
    const response = await this.api.get(`/api/accounts/${accountId}/communications?page=${page}&per_page=${perPage}`);
    return response.data;
  }

  async adminGetOrderServiceActiveEmail(): Promise<{ email_subject: string; email_body_html: string }> {
    const response = await this.api.get('/api/admin/orders/settings/order-service-active-email');
    return response.data;
  }

  async adminPutOrderServiceActiveEmail(body: {
    email_subject: string;
    email_body_html: string;
  }): Promise<{ email_subject: string; email_body_html: string }> {
    const response = await this.api.put('/api/admin/orders/settings/order-service-active-email', body);
    return response.data;
  }

  async getDidProductMappings(): Promise<{ mappings: { block_size: number; product_id: string }[] }> {
    const response = await this.api.get('/api/admin/orders/settings/did-product-mappings');
    return response.data;
  }

  async setDidProductMappings(
    mappings: { block_size: number; product_id: string }[],
  ): Promise<{ mappings: { block_size: number; product_id: string }[] }> {
    const response = await this.api.put('/api/admin/orders/settings/did-product-mappings', { mappings });
    return response.data;
  }

  getOrderItemPortingDocumentUrl(orderId: string, cartLineIndex: number, instanceIndex: number): string {
    const base = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
    return `${base}/api/admin/orders/${orderId}/items/${cartLineIndex}/${instanceIndex}/porting-document`;
  }

  async fetchPortingDocumentBlob(orderId: string, cartLineIndex: number, instanceIndex: number): Promise<{ objectUrl: string; isPdf: boolean }> {
    const path = `/api/admin/orders/${orderId}/items/${cartLineIndex}/${instanceIndex}/porting-document`;
    const response = await this.api.get(path, { responseType: 'blob' });
    const contentType: string = String(response.headers['content-type'] || '');
    const blob: Blob = response.data;
    const objectUrl = URL.createObjectURL(blob);
    return { objectUrl, isPdf: contentType.includes('pdf') };
  }

  // ── Account Documents ───────────────────────────────────────────────────

  async listAccountDocuments(accountId: string): Promise<AccountDocument[]> {
    const response = await this.api.get(`/api/accounts/${accountId}/documents`);
    return response.data;
  }

  async uploadAccountDocument(
    accountId: string,
    file: File,
    name: string,
    category: string,
    description: string,
  ): Promise<AccountDocument> {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('name', name);
    fd.append('category', category);
    fd.append('description', description);
    const response = await this.api.post(`/api/accounts/${accountId}/documents`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  async updateAccountDocument(
    accountId: string,
    docId: string,
    body: { name?: string; category?: string; description?: string },
  ): Promise<AccountDocument> {
    const response = await this.api.patch(`/api/accounts/${accountId}/documents/${docId}`, body);
    return response.data;
  }

  async deleteAccountDocument(accountId: string, docId: string): Promise<void> {
    await this.api.delete(`/api/accounts/${accountId}/documents/${docId}`);
  }

  accountDocumentFileUrl(accountId: string, docId: string): string {
    return `/api/accounts/${accountId}/documents/${docId}/file`;
  }

  async fetchDocumentBlobUrl(url: string): Promise<string> {
    const response = await this.api.get(url, { responseType: 'blob' });
    return URL.createObjectURL(response.data);
  }

  async listOrderConfirmationDocuments(orderId: string): Promise<AccountDocument[]> {
    const response = await this.api.get(`/api/admin/orders/${orderId}/confirmation-documents`);
    return response.data;
  }

  // -------------------------------------------------------------------------
  // Knowledge Base
  // -------------------------------------------------------------------------

  async kbListCategories(): Promise<any[]> {
    const r = await this.api.get('/api/kb/categories');
    return r.data;
  }
  async kbCreateCategory(data: { name: string; description?: string; sort_order?: number }): Promise<any> {
    const fd = new FormData();
    fd.append('name', data.name);
    if (data.description) fd.append('description', data.description);
    if (data.sort_order !== undefined) fd.append('sort_order', String(data.sort_order));
    const r = await this.api.post('/api/kb/categories', fd);
    return r.data;
  }
  async kbUpdateCategory(id: string, data: { name?: string; description?: string; sort_order?: number }): Promise<any> {
    const fd = new FormData();
    if (data.name) fd.append('name', data.name);
    if (data.description !== undefined) fd.append('description', data.description);
    if (data.sort_order !== undefined) fd.append('sort_order', String(data.sort_order));
    const r = await this.api.patch(`/api/kb/categories/${id}`, fd);
    return r.data;
  }
  async kbDeleteCategory(id: string): Promise<void> {
    await this.api.delete(`/api/kb/categories/${id}`);
  }

  async kbListTags(): Promise<any[]> {
    const r = await this.api.get('/api/kb/tags');
    return r.data;
  }
  async kbCreateTag(name: string): Promise<any> {
    const fd = new FormData();
    fd.append('name', name);
    const r = await this.api.post('/api/kb/tags', fd);
    return r.data;
  }
  async kbDeleteTag(id: string): Promise<void> {
    await this.api.delete(`/api/kb/tags/${id}`);
  }

  async kbListArticles(params?: {
    status?: string;
    category_id?: string;
    tag_id?: string;
    search?: string;
    is_public?: boolean;
    limit?: number;
    offset?: number;
  }): Promise<{ articles: any[]; total: number }> {
    const r = await this.api.get('/api/kb/articles', { params });
    return r.data;
  }
  async kbGetArticle(id: string): Promise<any> {
    const r = await this.api.get(`/api/kb/articles/${id}`);
    return r.data;
  }
  async kbCreateArticle(data: {
    title: string;
    content?: string;
    excerpt?: string;
    status?: string;
    is_public?: boolean;
    category_id?: string;
    expires_at?: string;
    tag_ids?: string;
  }): Promise<any> {
    const fd = new FormData();
    fd.append('title', data.title);
    if (data.content !== undefined) fd.append('content', data.content);
    if (data.excerpt !== undefined) fd.append('excerpt', data.excerpt);
    if (data.status) fd.append('status', data.status);
    fd.append('is_public', String(data.is_public ?? false));
    if (data.category_id) fd.append('category_id', data.category_id);
    if (data.expires_at) fd.append('expires_at', data.expires_at);
    if (data.tag_ids !== undefined) fd.append('tag_ids', data.tag_ids);
    const r = await this.api.post('/api/kb/articles', fd);
    return r.data;
  }
  async kbUpdateArticle(id: string, data: {
    title?: string;
    content?: string;
    excerpt?: string;
    status?: string;
    is_public?: boolean;
    category_id?: string | null;
    expires_at?: string | null;
    tag_ids?: string;
  }): Promise<any> {
    const fd = new FormData();
    if (data.title !== undefined) fd.append('title', data.title);
    if (data.content !== undefined) fd.append('content', data.content);
    if (data.excerpt !== undefined) fd.append('excerpt', data.excerpt);
    if (data.status !== undefined) fd.append('status', data.status);
    if (data.is_public !== undefined) fd.append('is_public', String(data.is_public));
    if (data.category_id === null) { fd.append('clear_category', 'true'); }
    else if (data.category_id !== undefined) fd.append('category_id', data.category_id);
    if (data.expires_at === null) { fd.append('clear_expires', 'true'); }
    else if (data.expires_at !== undefined) fd.append('expires_at', data.expires_at);
    if (data.tag_ids !== undefined) fd.append('tag_ids', data.tag_ids);
    const r = await this.api.patch(`/api/kb/articles/${id}`, fd);
    return r.data;
  }
  async kbDeleteArticle(id: string): Promise<void> {
    await this.api.delete(`/api/kb/articles/${id}`);
  }

  async kbUploadAttachment(articleId: string, file: File, displayName?: string): Promise<any> {
    const fd = new FormData();
    fd.append('file', file);
    if (displayName) fd.append('display_name', displayName);
    const r = await this.api.post(`/api/kb/articles/${articleId}/attachments`, fd);
    return r.data;
  }
  kbAttachmentDownloadUrl(articleId: string, attachmentId: string): string {
    return `/api/kb/articles/${articleId}/attachments/${attachmentId}/download`;
  }
  async kbDeleteAttachment(articleId: string, attachmentId: string): Promise<void> {
    await this.api.delete(`/api/kb/articles/${articleId}/attachments/${attachmentId}`);
  }

  // -------------------------------------------------------------------------
  // Google Review Requests
  // -------------------------------------------------------------------------

  async adminGetGoogleReviewSettings(): Promise<any> {
    const r = await this.api.get('/api/admin/google-reviews/settings');
    return r.data;
  }
  async adminSaveGoogleReviewSettings(data: Record<string, string>): Promise<void> {
    await this.api.put('/api/admin/google-reviews/settings', data);
  }
  async adminTestGoogleReviewConnection(): Promise<any> {
    const r = await this.api.post('/api/admin/google-reviews/test-connection');
    return r.data;
  }
  async adminGetGoogleReviewStats(): Promise<any | null> {
    const r = await this.api.get('/api/admin/google-reviews/stats');
    return r.data;
  }
  async adminSendGoogleReviewRequest(accountId: string): Promise<{ ok: boolean; sent_to: string }> {
    const r = await this.api.post(`/api/admin/google-reviews/accounts/${accountId}/send-review-request`);
    return r.data;
  }

  async sendAccountSms(accountId: string, message: string, contactId?: string | null): Promise<{ ok: boolean; sent_to: string }> {
    const r = await this.api.post(`/api/accounts/${accountId}/send-sms`, { message, contact_id: contactId || undefined });
    return r.data;
  }

  async getAccountNotes(accountId: string, page = 1, perPage = 25): Promise<{
    notes: {
      id: string;
      content: string;
      created_at: string;
      created_by: string | null;
      created_by_name: string | null;
      source: 'staff' | 'portal';
    }[];
    total: number;
    page: number;
    per_page: number;
  }> {
    const response = await this.api.get(`/api/accounts/${accountId}/notes?page=${page}&per_page=${perPage}`);
    return response.data;
  }

  async addAccountNote(accountId: string, content: string): Promise<{
    id: string;
    content: string;
    created_at: string;
    created_by: string | null;
    created_by_name: string | null;
  }> {
    const response = await this.api.post(`/api/accounts/${accountId}/notes`, { content });
    return response.data;
  }

  async deleteAccountNote(accountId: string, noteId: string): Promise<{ ok: boolean }> {
    const response = await this.api.delete(`/api/accounts/${accountId}/notes/${noteId}`);
    return response.data;
  }

  async adminGetPersistedReviews(): Promise<any[]> {
    const r = await this.api.get('/api/admin/google-reviews/reviews');
    return r.data;
  }

  async adminGetAccountReviews(accountId: string): Promise<any[]> {
    const r = await this.api.get(`/api/admin/google-reviews/account/${accountId}/reviews`);
    return r.data;
  }

  async adminGetReviewLinks(): Promise<any[]> {
    const r = await this.api.get('/api/admin/google-reviews/links');
    return r.data;
  }

  async adminCreateReviewLink(reviewId: string, accountId: string, reviewerName?: string): Promise<any> {
    const r = await this.api.post('/api/admin/google-reviews/links', {
      review_id: reviewId,
      account_id: accountId,
      reviewer_name: reviewerName,
    });
    return r.data;
  }

  async adminDeleteReviewLink(reviewId: string): Promise<void> {
    await this.api.delete(`/api/admin/google-reviews/links/${encodeURIComponent(reviewId)}`);
  }

  async adminTriggerReviewSync(): Promise<void> {
    await this.api.post('/api/admin/google-reviews/sync');
  }

  // -------------------------------------------------------------------------
  // Nightly DB backup
  // -------------------------------------------------------------------------

  async adminGetDbBackupSettings(): Promise<{
    enabled: boolean;
    hour_utc: number;
    last_run_at: string | null;
    last_status: string | null;
    last_error: string | null;
    last_size_bytes: number | null;
  }> {
    const r = await this.api.get('/api/admin/db-backup/settings');
    return r.data;
  }

  async adminSaveDbBackupSettings(data: { enabled: boolean; hour_utc: number }): Promise<void> {
    await this.api.put('/api/admin/db-backup/settings', data);
  }

  async adminTriggerDbBackup(): Promise<{ ok: boolean; size_bytes: number }> {
    const r = await this.api.post('/api/admin/db-backup/run');
    return r.data;
  }

  // -------------------------------------------------------------------------
  // Admin user management
  // -------------------------------------------------------------------------

  async adminListUsers(): Promise<any[]> {
    const r = await this.api.get('/api/auth/admin/users');
    return r.data;
  }
  async adminUpdateUser(userId: string, data: {
    display_name?: string;
    email?: string;
    is_active?: boolean;
    is_admin?: boolean;
    can_author_kb?: boolean;
  }): Promise<any> {
    const r = await this.api.patch(`/api/auth/admin/users/${userId}`, data);
    return r.data;
  }
  async adminCreateUser(data: {
    username: string;
    password: string;
    display_name?: string;
    email?: string;
    is_admin?: boolean;
    can_author_kb?: boolean;
  }): Promise<any> {
    const r = await this.api.post('/api/auth/admin/users', null, { params: data });
    return r.data;
  }
}

const apiServiceInstance = new ApiService();
export default apiServiceInstance;
export type { SupaSQBuilding, SupaSQUnit, SupaSQResponse } from '../types/supa';